import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType } from '@capacitor/camera';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { getTCGdexEndpoint } from '../utils/setsByYear';
import { findCard, searchLocalByNumber } from '../utils/cardLookup';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useGuestPrompt } from '../context/GuestPromptContext';
import { useProfile } from '../context/ProfileContext';
import CameraService from '../services/CameraService';
import { detectCardInGuide } from '../lib/cardDetector';
import { titanIdentify } from '../services/titanIdentify';
import AddParentModal from '../components/AddParentModal';
import { fetchPricing, fetchGradedPricing } from '../lib/fetchPricing';
import { isGradedCard, fetchAndCacheSingleCard, getBulkCardPrices, getPriceForCard } from '../services/cardPriceCache';
import TradePricingOverlay from '../components/fairtrade/TradePricingOverlay';
import { generatePriceReportPdf } from '../lib/pdfExport';

// Phases
const PHASE = {
  CAPTURE: 'capture',
  PICKING: 'picking',           // Native picker open, prevents camera restart
  LIBRARY_CROP: 'library_crop', // Sequential crop with frame guide
  COLLECTION_PICK: 'collection_pick', // Pick from own collection (trade mode)
  GALLERY: 'gallery',
  CARD_FAN: 'card_fan', // Full-screen alternate match picker
  DETAILS: 'details',
  PRICE_RESULTS: 'price_results',
  DONE: 'done',
};

const DEFAULT_MAX_CARDS = 20;
const TRADE_MAX_CARDS = 5;
const QUICKSCAN_MAX_CARDS = 100;

// Grade/condition options (same as Scanner.js)
const RAW_CONDITIONS = ['NM', 'LP', 'MP', 'HP', 'DMG'];
const PSA_GRADES = [
  { value: 'PSA 10', label: 'PSA 10 - Gem Mint' },
  { value: 'PSA 9', label: 'PSA 9 - Mint' },
  { value: 'PSA 8', label: 'PSA 8 - NM-MT' },
  { value: 'PSA 7', label: 'PSA 7 - NM' },
  { value: 'PSA 6', label: 'PSA 6 - EX-MT' },
  { value: 'PSA 5', label: 'PSA 5 - EX' },
];
const CGC_GRADES = [
  { value: 'CGC 10', label: 'CGC 10 - Pristine' },
  { value: 'CGC 9.5', label: 'CGC 9.5 - Gem Mint' },
  { value: 'CGC 9', label: 'CGC 9 - Mint' },
  { value: 'CGC 8.5', label: 'CGC 8.5 - NM-MT+' },
  { value: 'CGC 8', label: 'CGC 8 - NM-MT' },
];
const BGS_GRADES = [
  { value: 'BGS 10', label: 'BGS 10 - Pristine' },
  { value: 'BGS 9.5', label: 'BGS 9.5 - Gem Mint' },
  { value: 'BGS 9', label: 'BGS 9 - Mint' },
  { value: 'BGS 8.5', label: 'BGS 8.5 - NM-MT+' },
  { value: 'BGS 8', label: 'BGS 8 - NM-MT' },
];

const BulkScanner = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { requireAuth } = useGuestPrompt();
  const { activeProfile, isViewerOnly } = useProfile();

  // Mode params
  const mode = searchParams.get('mode');
  const side = searchParams.get('side');
  const isTradeMode = mode === 'trade';
  const isQuickScan = mode === 'quickscan';
  const MAX_CARDS = isQuickScan ? QUICKSCAN_MAX_CARDS : isTradeMode ? TRADE_MAX_CARDS : DEFAULT_MAX_CARDS;

  // Phase
  const [phase, setPhase] = useState(PHASE.CAPTURE);

  // Camera
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraFailed, setCameraFailed] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  // Language & card type
  const [scanLang, setScanLang] = useState('en');
  const [scanType, setScanType] = useState('raw');

  // Cards batch
  const [cards, setCards] = useState([]);
  const [retakeIndex, setRetakeIndex] = useState(null);

  // Card fan
  const [fanCardId, setFanCardId] = useState(null);
  const [fanScrollIndex, setFanScrollIndex] = useState(0);

  // Fan manual search
  const [fanSearchMode, setFanSearchMode] = useState(false);
  const [fanSearchQuery, setFanSearchQuery] = useState('');
  const [fanSearchResults, setFanSearchResults] = useState([]);
  const [isFanSearching, setIsFanSearching] = useState(false);

  // Translator
  const [showTranslator, setShowTranslator] = useState(false);
  const [translateFrom, setTranslateFrom] = useState('en');
  const [translateTo, setTranslateTo] = useState('ja');
  const [translateInput, setTranslateInput] = useState('');
  const [translateOutput, setTranslateOutput] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);

  // Details
  const [detailsIndex, setDetailsIndex] = useState(0);
  const [binders, setBinders] = useState([]);
  const [showBinderPicker, setShowBinderPicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [showParentModal, setShowParentModal] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [error, setError] = useState(null);

  // Quick Scan pricing state
  const [pricingCards, setPricingCards] = useState([]);
  const [pricingProgress, setPricingProgress] = useState(0);
  const [pricingDetailCard, setPricingDetailCard] = useState(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // Capture animation
  const [flyingCard, setFlyingCard] = useState(null); // { image, startRect }
  const [showFlash, setShowFlash] = useState(false);
  const badgeRef = useRef(null);

  // Refs
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const cropCanvasRef = useRef(null);
  const frameRef = useRef(null);
  const [savedFrameRect, setSavedFrameRect] = useState(null);
  const fanScrollRef = useRef(null);

  // Card fill detection
  const [cardFill, setCardFill] = useState({ detected: false, edgeCount: 0, coverage: { top: 0, bottom: 0, left: 0, right: 0 } });
  const detectCanvasRef = useRef(null);
  const detectIntervalRef = useRef(null);

  // Library picker + crop state
  const [libraryPhotos, setLibraryPhotos] = useState([]);
  const [libraryCropIndex, setLibraryCropIndex] = useState(0);
  const croppedLibraryRef = useRef([]);
  const transformRef = useRef(null);
  const pickCropImgRef = useRef(null);
  const pickCropFrameRef = useRef(null);

  // Contribution prompt state
  const [showContributePrompt, setShowContributePrompt] = useState(false);
  const [contributeCardId, setContributeCardId] = useState(null);

  // Manual card entry state
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualNumber, setManualNumber] = useState('');
  const [manualSetName, setManualSetName] = useState('');
  const [manualLanguage, setManualLanguage] = useState('en');
  const [manualSaving, setManualSaving] = useState(false);

  // Collection picker state (trade mode)
  const [collectionCards, setCollectionCards] = useState([]);
  const [collectionSelected, setCollectionSelected] = useState(new Set());
  const [collectionSearch, setCollectionSearch] = useState('');
  const [collectionLoading, setCollectionLoading] = useState(false);

  // Details phase: all cards that entered details (confirmed + already saved)
  const detailsCards = cards.filter(c => c.status === 'confirmed' || c.status === 'saved');
  const currentDetailCard = detailsCards.find(c => c.status === 'confirmed');
  const detailsSavedCount = detailsCards.filter(c => c.status === 'saved').length;

  // Helper: get grade options
  const getGradeOptions = (company) => {
    if (company === 'PSA') return PSA_GRADES;
    if (company === 'CGC') return CGC_GRADES;
    return BGS_GRADES;
  };

  // ==========================================
  // CAMERA LIFECYCLE
  // ==========================================

  const startCamera = useCallback(async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        await CameraService.show('bulk-camera-view');
        await new Promise(r => setTimeout(r, 200));
        setCameraReady(true);
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setCameraReady(true);
        }
      }
    } catch (err) {
      console.error('[BulkScanner] Camera error:', err);
      setCameraFailed(true);
    }
  }, []);

  const stopCamera = useCallback(async () => {
    if (!cameraReady) return;
    try {
      setCameraReady(false);
      if (Capacitor.isNativePlatform()) {
        await CameraService.hide();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    } catch (e) {
      if (!e?.message?.includes('already stopped')) {
        console.error('[BulkScanner] Stop camera error:', e);
      }
    }
  }, [cameraReady]);

  // Body transparency for native camera
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      document.body.classList.add('camera-active');
      document.documentElement.style.backgroundColor = 'transparent';
    }
    return () => {
      document.body.classList.remove('camera-active');
      document.documentElement.style.backgroundColor = '';
    };
  }, []);

  // Start camera when in capture phase
  useEffect(() => {
    if (phase === PHASE.CAPTURE && !cameraReady && !cameraFailed) {
      startCamera();
    }
  }, [phase, cameraReady, cameraFailed, startCamera]);

  // Hide camera when leaving capture phase, show when returning
  useEffect(() => {
    if (phase !== PHASE.CAPTURE && phase !== PHASE.PICKING && cameraReady) {
      if (Capacitor.isNativePlatform()) {
        CameraService.hide().catch(() => {});
      }
      setCameraReady(false);
    }
  }, [phase, cameraReady]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (Capacitor.isNativePlatform()) {
        CameraService.hide().catch(() => {});
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Save frame rect
  useEffect(() => {
    if (phase === PHASE.CAPTURE && frameRef.current && cameraReady) {
      const rect = frameRef.current.getBoundingClientRect();
      setSavedFrameRect({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      });
    }
  }, [phase, cameraReady]);

  // Card fill detection loop
  useEffect(() => {
    if (phase !== PHASE.CAPTURE || !cameraReady) {
      if (detectIntervalRef.current) {
        clearInterval(detectIntervalRef.current);
        detectIntervalRef.current = null;
      }
      setCardFill({ detected: false, edgeCount: 0, coverage: { top: 0, bottom: 0, left: 0, right: 0 } });
      return;
    }

    const ANALYSIS_W = 240;
    const EDGE_THRESH = 20;
    let logCount = 0;

    // Create offscreen canvas once
    if (!detectCanvasRef.current) {
      detectCanvasRef.current = document.createElement('canvas');
    }

    const runDetection = async () => {
      try {
        const frameEl = frameRef.current;
        if (!frameEl) return;

        const frameRect = frameEl.getBoundingClientRect();
        if (frameRect.width < 10 || frameRect.height < 10) return;

        const screenW = window.innerWidth;
        const screenH = window.innerHeight;
        const canvas = detectCanvasRef.current;
        const ctx = canvas.getContext('2d');

        let imgEl;

        if (Capacitor.isNativePlatform()) {
          // Native: grab a sample from the plugin (quality 50 for reliable detection)
          const sampleValue = await CameraService.captureSample(50);
          if (!sampleValue) return;

          // Plugin may return file path or base64 depending on storeToFile setting
          let imgSrc;
          if (sampleValue.startsWith('file://') || sampleValue.startsWith('/')) {
            imgSrc = Capacitor.convertFileSrc(sampleValue);
          } else {
            imgSrc = `data:image/jpeg;base64,${sampleValue}`;
          }

          imgEl = new Image();
          imgEl.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            imgEl.onload = resolve;
            imgEl.onerror = reject;
            imgEl.src = imgSrc;
          });
        } else {
          // Web: read from video element
          const video = videoRef.current;
          if (!video || video.readyState < 2) return;
          imgEl = video;
        }

        // Get source dimensions
        const srcW = imgEl.videoWidth || imgEl.naturalWidth || imgEl.width;
        const srcH = imgEl.videoHeight || imgEl.naturalHeight || imgEl.height;
        if (!srcW || !srcH) return;

        // Scale to analysis width preserving aspect ratio
        const scale = ANALYSIS_W / srcW;
        const analysisH = Math.round(srcH * scale);
        canvas.width = ANALYSIS_W;
        canvas.height = analysisH;
        ctx.drawImage(imgEl, 0, 0, ANALYSIS_W, analysisH);

        // Map frame rect from screen coords to analysis pixel coords
        let guideX, guideY, guideW, guideH;

        if (Capacitor.isNativePlatform()) {
          // Use CameraService.getCropRect (proven mapping) then scale to analysis
          const cropRect = CameraService.getCropRect(
            { x: frameRect.left, y: frameRect.top, width: frameRect.width, height: frameRect.height },
            srcW, srcH
          );
          guideX = cropRect.x * scale;
          guideY = cropRect.y * scale;
          guideW = cropRect.width * scale;
          guideH = cropRect.height * scale;
        } else {
          // Web: video covers the screen (object-fit: cover)
          const videoAspect = srcW / srcH;
          const screenAspect = screenW / screenH;
          let visibleW, visibleH, offsetX, offsetY;
          if (videoAspect > screenAspect) {
            visibleH = srcH;
            visibleW = srcH * screenAspect;
            offsetX = (srcW - visibleW) / 2;
            offsetY = 0;
          } else {
            visibleW = srcW;
            visibleH = srcW / screenAspect;
            offsetX = 0;
            offsetY = (srcH - visibleH) / 2;
          }
          const mapX = (sx) => (offsetX + (sx / screenW) * visibleW) * scale;
          const mapY = (sy) => (offsetY + (sy / screenH) * visibleH) * scale;
          guideX = mapX(frameRect.left);
          guideY = mapY(frameRect.top);
          guideW = mapX(frameRect.right) - guideX;
          guideH = mapY(frameRect.bottom) - guideY;
        }

        // Clamp guide to canvas bounds
        guideX = Math.max(0, Math.min(ANALYSIS_W - 2, guideX));
        guideY = Math.max(0, Math.min(analysisH - 2, guideY));
        guideW = Math.min(guideW, ANALYSIS_W - guideX);
        guideH = Math.min(guideH, analysisH - guideY);

        // Sanity: guide must be reasonable size
        if (guideW < 20 || guideH < 20 || guideW > ANALYSIS_W * 0.95 || guideH > analysisH * 0.95) {
          if (logCount < 3) {
            console.warn('[DETECT] Guide rect out of bounds:', { guideX, guideY, guideW, guideH, ANALYSIS_W, analysisH });
            logCount++;
          }
          return;
        }

        if (logCount < 5) {
          console.log('[DETECT] img:', srcW, 'x', srcH, '-> analysis:', ANALYSIS_W, 'x', analysisH,
            '| guide:', Math.round(guideX), Math.round(guideY), Math.round(guideW), 'x', Math.round(guideH));
          logCount++;
        }

        const pixels = ctx.getImageData(0, 0, ANALYSIS_W, analysisH).data;
        const result = detectCardInGuide(
          pixels,
          { x: Math.round(guideX), y: Math.round(guideY), w: Math.round(guideW), h: Math.round(guideH) },
          ANALYSIS_W,
          analysisH
        );

        if (logCount < 8) {
          console.log('[DETECT] scores:', result.coverage, 'edges:', result.edgeCount, 'detected:', result.detected);
          logCount++;
        }

        setCardFill({
          detected: result.detected,
          edgeCount: result.edgeCount,
          coverage: {
            top: result.coverage.top > EDGE_THRESH,
            bottom: result.coverage.bottom > EDGE_THRESH,
            left: result.coverage.left > EDGE_THRESH,
            right: result.coverage.right > EDGE_THRESH,
          },
        });
      } catch (e) {
        console.warn('[DETECT] Error:', e.message);
      }
    };

    // Small delay to let frame render, then start detection
    const startTimer = setTimeout(() => {
      runDetection();
      detectIntervalRef.current = setInterval(runDetection, 500);
    }, 600);

    return () => {
      clearTimeout(startTimer);
      if (detectIntervalRef.current) {
        clearInterval(detectIntervalRef.current);
        detectIntervalRef.current = null;
      }
    };
  }, [phase, cameraReady]);

  // Fetch binders on mount (skip in trade mode and quickscan)
  useEffect(() => {
    if (isTradeMode || isQuickScan) return;
    const fetchBinders = async () => {
      const profileId = activeProfile?.id;
      if (!profileId) return;
      try {
        const { data, error } = await supabase
          .from('binders')
          .select('*')
          .eq('user_id', profileId)
          .order('name');
        if (!error && data) setBinders(data);
      } catch (e) {
        console.log('[BulkScanner] Failed to fetch binders:', e);
      }
    };
    fetchBinders();
  }, [activeProfile?.id, isTradeMode, isQuickScan]);

  // Force PC for viewer-only (skip in trade mode and quickscan)
  useEffect(() => {
    if (isTradeMode || isQuickScan) return;
    if (isViewerOnly) {
      setCards(prev => prev.map(c => ({ ...c, tradingStatus: 'PC' })));
    }
  }, [isViewerOnly, isTradeMode, isQuickScan]);

  // ==========================================
  // IMAGE PROCESSING
  // ==========================================

  const compressImage = (dataUrl, maxWidth = 1024, quality = 0.7) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  };

  const cropToFrame = (fullImage, type = 'raw') => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        CameraService.sensorAspect = img.width / img.height;

        const cropCanvas = cropCanvasRef.current;
        if (!cropCanvas) {
          resolve({ embedCrop: fullImage, displayCrop: fullImage });
          return;
        }

        try {
          const imgWidth = img.width;
          const imgHeight = img.height;
          const screenWidth = window.innerWidth;
          const screenHeight = window.innerHeight;
          const frameRect = savedFrameRect || {
            x: screenWidth * 0.125,
            y: screenHeight * 0.2,
            width: screenWidth * 0.75,
            height: screenWidth * 0.75 * (7 / 5),
          };

          // Embed crop: exactly the guide frame (for matching)
          const embedRect = CameraService.getCropRect(frameRect, imgWidth, imgHeight);
          cropCanvas.width = embedRect.width;
          cropCanvas.height = embedRect.height;
          const ctx = cropCanvas.getContext('2d');
          ctx.drawImage(img, embedRect.x, embedRect.y, embedRect.width, embedRect.height, 0, 0, embedRect.width, embedRect.height);
          const embedCrop = cropCanvas.toDataURL('image/jpeg', 0.9);

          // Display crop: wider area for user photo (toploader/slab visibility)
          let padTop, padBottom, padLeft, padRight;
          if (type === 'slab') {
            padTop = frameRect.height * 0.40;
            padBottom = frameRect.height * 0.15;
            padLeft = frameRect.width * 0.12;
            padRight = frameRect.width * 0.12;
          } else {
            padTop = frameRect.height * 0.08;
            padBottom = frameRect.height * 0.08;
            padLeft = frameRect.width * 0.10;
            padRight = frameRect.width * 0.10;
          }

          const displayFrameRect = {
            x: frameRect.x - padLeft,
            y: frameRect.y - padTop,
            width: frameRect.width + padLeft + padRight,
            height: frameRect.height + padTop + padBottom,
          };
          const displayRect = CameraService.getCropRect(displayFrameRect, imgWidth, imgHeight);
          cropCanvas.width = displayRect.width;
          cropCanvas.height = displayRect.height;
          ctx.drawImage(img, displayRect.x, displayRect.y, displayRect.width, displayRect.height, 0, 0, displayRect.width, displayRect.height);
          const displayCrop = cropCanvas.toDataURL('image/jpeg', 0.9);

          resolve({ embedCrop, displayCrop });
        } catch (e) {
          console.error('[BulkScanner] Crop error:', e);
          resolve({ embedCrop: fullImage, displayCrop: fullImage });
        }
      };
      img.onerror = () => resolve({ embedCrop: fullImage, displayCrop: fullImage });
      setTimeout(() => {
        if (!img.complete) resolve({ embedCrop: fullImage, displayCrop: fullImage });
      }, 5000);
      img.src = fullImage;
    });
  };

  // ==========================================
  // BACKGROUND IDENTIFICATION
  // ==========================================

  const updateCard = useCallback((cardId, updates) => {
    setCards(prev => prev.map(c => c.id === cardId ? { ...c, ...updates } : c));
  }, []);

  const identifyInBackground = useCallback(async (cardId, croppedImage) => {
    try {
      const compressed = await compressImage(croppedImage, 1024, 0.7);
      const matches = await titanIdentify(compressed, 5, scanLang);

      if (!matches || matches.length === 0) {
        updateCard(cardId, { status: 'error', error: 'No matches found' });
        return;
      }

      // Build placeholders immediately
      const placeholders = matches.map(m => ({
        name: m.name,
        originalName: '',
        language: scanLang,
        setName: m.set_name,
        setCode: m.set_id,
        number: m.local_id,
        confidence: Math.round((m.similarity || 0) * 100),
        image: null,
        imageHigh: null,
        tcgId: null,
        source: null,
        loading: true,
      }));

      updateCard(cardId, { titanMatches: matches, candidatesWithData: placeholders, status: 'ready' });

      // Enrich progressively
      matches.forEach(async (m, idx) => {
        const result = await findCard(m.name, m.local_id, m.set_name, m.set_id, scanLang, m.name);
        if (result) {
          setCards(prev => prev.map(c => {
            if (c.id !== cardId) return c;
            const updated = [...c.candidatesWithData];
            updated[idx] = {
              ...updated[idx],
              name: result.name || m.name,
              setName: result.setName || m.set_name,
              setCode: result.setCode || m.set_id,
              number: result.localId || result.number || m.local_id,
              image: result.image,
              imageHigh: result.imageHigh,
              tcgId: result.tcgId,
              source: result.source,
              localId: result.localId,
              loading: false,
            };
            return { ...c, candidatesWithData: updated };
          }));
        } else {
          setCards(prev => prev.map(c => {
            if (c.id !== cardId) return c;
            const updated = [...c.candidatesWithData];
            updated[idx] = { ...updated[idx], loading: false };
            return { ...c, candidatesWithData: updated };
          }));
        }
      });
    } catch (err) {
      console.error('[BulkScanner] Identify error:', err?.message || err, JSON.stringify(err));
      updateCard(cardId, { status: 'error', error: err?.message || 'Unknown error' });
    }
  }, [updateCard, scanLang]);

  // ==========================================
  // CAPTURE
  // ==========================================

  const capturePhoto = async () => {
    if (isCapturing || !cameraReady) return;
    if (cards.length >= MAX_CARDS && retakeIndex === null) return;

    setIsCapturing(true);
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 150);
    if (Capacitor.isNativePlatform()) {
      try { Haptics.impact({ style: ImpactStyle.Light }); } catch (e) {}
    }

    try {
      let fullImageData;

      if (Capacitor.isNativePlatform()) {
        const captureResult = await CameraService.capture();
        const path = captureResult.filePath;
        if (!path) {
          throw new Error('No capture data returned');
        }
        // storeToFile mode returns a file path: `file://...` on iOS, a bare
        // absolute path (`/data/.../cpcp_*.jpg`) on Android. Base64 is only the
        // fallback when the native file write fails. A real path is short; a
        // base64 JPEG is always many KB, so length disambiguates the rare case
        // where a base64 string happens to start with `/` (JPEG -> `/9j/`).
        if (path.startsWith('file://')) {
          fullImageData = Capacitor.convertFileSrc(path);
        } else if (path.startsWith('/') && path.length < 1024) {
          fullImageData = Capacitor.convertFileSrc(`file://${path}`);
        } else {
          fullImageData = `data:image/jpeg;base64,${path}`;
        }
      } else {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (canvas && video) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          canvas.getContext('2d').drawImage(video, 0, 0);
          fullImageData = canvas.toDataURL('image/jpeg', 0.95);
        }
      }

      if (!fullImageData) throw new Error('No image data captured');

      const { embedCrop, displayCrop } = await cropToFrame(fullImageData, scanType);
      const cardId = crypto.randomUUID();

      const newCard = {
        id: cardId,
        croppedImage: displayCrop,
        scanType: scanType,
        titanMatches: [],
        candidatesWithData: [],
        selectedMatchIndex: null,
        status: 'identifying',
        error: null,
        variant: 'Normal',
        condition: 'NM',
        gradeType: 'raw',
        gradingCompany: 'PSA',
        specification: null,
        tradingStatus: isViewerOnly ? 'PC' : 'TRADE',
        binder: null,
        availableVariants: [],
      };

      // Start fly animation (from frame center toward badge)
      const frameEl = frameRef.current;
      if (frameEl && retakeIndex === null) {
        const rect = frameEl.getBoundingClientRect();
        setFlyingCard({ image: displayCrop, startRect: rect });
      }

      if (retakeIndex !== null) {
        // Replace the card at retakeIndex, then navigate to its card fan
        setCards(prev => {
          const updated = [...prev];
          updated[retakeIndex] = newCard;
          return updated;
        });
        setRetakeIndex(null);

        // Fire background identification
        identifyInBackground(cardId, embedCrop);

        // Auto-navigate to card fan for this retaken card
        setFanCardId(cardId);
        setFanScrollIndex(0);
        setPhase(PHASE.CARD_FAN);
      } else {
        setCards(prev => [...prev, newCard]);

        // Fire background identification
        identifyInBackground(cardId, embedCrop);

        // Auto-transition to gallery at max
        if (cards.length + 1 >= MAX_CARDS) {
          setTimeout(() => setPhase(PHASE.GALLERY), 500);
        }
      }
    } catch (e) {
      console.error('[BulkScanner] Capture error:', e);
      setError('Capture failed: ' + e.message);
    }

    setIsCapturing(false);
  };

  // Called when fly animation completes (card lands on badge)
  const onFlyComplete = () => {
    setFlyingCard(null);
    if (Capacitor.isNativePlatform()) {
      Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    }
  };

  // ==========================================
  // LIBRARY PICKER + CROP
  // ==========================================

  const pickFromLibrary = async () => {
    try {
      setPhase(PHASE.PICKING);

      // Stop camera while picking
      if (cameraReady) {
        if (Capacitor.isNativePlatform()) {
          await CameraService.hide().catch(() => {});
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
        setCameraReady(false);
      }

      const limit = MAX_CARDS - cards.length;
      if (limit <= 0) {
        setPhase(PHASE.CAPTURE);
        return;
      }

      const result = await Camera.pickImages({
        quality: 95,
        limit,
        correctOrientation: true,
      });

      if (!result.photos || result.photos.length === 0) {
        setPhase(PHASE.CAPTURE);
        return;
      }

      const paths = result.photos.map(p => p.webPath);
      setLibraryPhotos(paths);
      setLibraryCropIndex(0);
      croppedLibraryRef.current = [];
      setPhase(PHASE.LIBRARY_CROP);
    } catch (err) {
      console.error('[BulkScanner] pickFromLibrary error:', err);
      setPhase(PHASE.CAPTURE);
    }
  };

  const cropCurrentPhoto = () => {
    const imgEl = pickCropImgRef.current;
    const frameEl = pickCropFrameRef.current;
    if (!imgEl || !frameEl) return;

    try {
      const imgRect = imgEl.getBoundingClientRect();
      const frameRect = frameEl.getBoundingClientRect();

      // Calculate the visible portion of the image that falls within the frame
      const naturalW = imgEl.naturalWidth;
      const naturalH = imgEl.naturalHeight;

      // Scale from displayed image to natural image
      const scaleX = naturalW / imgRect.width;
      const scaleY = naturalH / imgRect.height;

      // Frame position relative to the image element
      let srcX = (frameRect.left - imgRect.left) * scaleX;
      let srcY = (frameRect.top - imgRect.top) * scaleY;
      let srcW = frameRect.width * scaleX;
      let srcH = frameRect.height * scaleY;

      // Clamp to image bounds
      srcX = Math.max(0, srcX);
      srcY = Math.max(0, srcY);
      srcW = Math.min(srcW, naturalW - srcX);
      srcH = Math.min(srcH, naturalH - srcY);

      if (srcW <= 0 || srcH <= 0) {
        // Frame is completely outside image, skip
        skipCurrentPhoto();
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = Math.round(srcW);
      canvas.height = Math.round(srcH);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(imgEl, Math.round(srcX), Math.round(srcY), Math.round(srcW), Math.round(srcH), 0, 0, Math.round(srcW), Math.round(srcH));

      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      croppedLibraryRef.current.push(dataUrl);

      // Reset zoom for next photo
      if (transformRef.current) {
        transformRef.current.resetTransform();
      }

      if (libraryCropIndex < libraryPhotos.length - 1) {
        setLibraryCropIndex(prev => prev + 1);
      } else {
        finishLibraryCrop();
      }
    } catch (err) {
      console.error('[BulkScanner] cropCurrentPhoto error:', err);
      skipCurrentPhoto();
    }
  };

  const skipCurrentPhoto = () => {
    if (transformRef.current) {
      transformRef.current.resetTransform();
    }
    if (libraryCropIndex < libraryPhotos.length - 1) {
      setLibraryCropIndex(prev => prev + 1);
    } else {
      finishLibraryCrop();
    }
  };

  const goBackCrop = () => {
    if (libraryCropIndex === 0) {
      // Cancel entire crop
      setLibraryPhotos([]);
      croppedLibraryRef.current = [];
      setPhase(PHASE.CAPTURE);
      return;
    }
    // Pop last cropped and go back
    croppedLibraryRef.current.pop();
    if (transformRef.current) {
      transformRef.current.resetTransform();
    }
    setLibraryCropIndex(prev => prev - 1);
  };

  const finishLibraryCrop = () => {
    const cropped = croppedLibraryRef.current;
    if (cropped.length === 0) {
      // All were skipped
      setLibraryPhotos([]);
      croppedLibraryRef.current = [];
      setPhase(PHASE.CAPTURE);
      return;
    }

    const newCards = cropped.map((dataUrl) => ({
      id: crypto.randomUUID(),
      croppedImage: dataUrl,
      titanMatches: [],
      candidatesWithData: [],
      selectedMatchIndex: null,
      status: 'identifying',
      error: null,
      variant: 'Normal',
      condition: 'NM',
      gradeType: 'raw',
      gradingCompany: 'PSA',
      specification: null,
      tradingStatus: isViewerOnly ? 'PC' : 'TRADE',
      binder: null,
      availableVariants: [],
    }));

    setCards(prev => [...prev, ...newCards]);

    // Fire identification with staggered timing
    newCards.forEach((card, i) => {
      setTimeout(() => {
        identifyInBackground(card.id, card.croppedImage);
      }, i * 200);
    });

    // Cleanup
    setLibraryPhotos([]);
    croppedLibraryRef.current = [];

    // Navigate - go to gallery to review the cards just added
    setPhase(PHASE.GALLERY);
  };

  // ==========================================
  // MANUAL CARD ENTRY
  // ==========================================

  const handleManualSave = async () => {
    if (requireAuth('save cards to your binder')) return;
    if (!manualName.trim() || !contributeCardId) return;

    setManualSaving(true);
    try {
      const card = cards.find(c => c.id === contributeCardId);
      const profileId = activeProfile?.id;
      if (!profileId) throw new Error('No active profile');

      // Upload their scanned photo
      let uploadedImageUrl = null;
      if (card?.croppedImage) {
        const blob = await fetch(card.croppedImage).then(r => r.blob());
        const filename = `${user.id}/${Date.now()}-manual-entry.jpg`;
        const { error: uploadError } = await supabase.storage
          .from('Pokemon Card Images')
          .upload(filename, blob, { contentType: 'image/jpeg' });
        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from('Pokemon Card Images')
            .getPublicUrl(filename);
          uploadedImageUrl = urlData?.publicUrl;
        }
      }

      const cardId = crypto.randomUUID();
      const cardData = {
        id: cardId,
        user_id: profileId,
        binder_id: null,
        card_id: null,
        name: manualName.trim(),
        variant: 'Normal',
        grade: 'NM',
        original_image_url: uploadedImageUrl,
        digital_image_url: null,
        tcgdex_image: null,
        set_name: manualSetName.trim() || '',
        set_number: manualNumber.trim() || null,
        language: manualLanguage,
        is_for_trade: false,
        trading_status: 'PC',
        traded: false,
        is_private: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { error: insertError } = await supabase.from('cards').insert(cardData);
      if (insertError) throw insertError;

      // Remove from scanner cards list and close
      removeCard(contributeCardId);
      setShowManualEntry(false);
      setShowContributePrompt(false);
      setManualName('');
      setManualNumber('');
      setManualSetName('');
      setManualLanguage('en');
      setContributeCardId(null);
    } catch (err) {
      console.error('[BulkScanner] Manual save error:', err);
      alert('Failed to save card. Please try again.');
    } finally {
      setManualSaving(false);
    }
  };

  // ==========================================
  // COLLECTION PICKER (Trade mode)
  // ==========================================

  const openCollectionPicker = async () => {
    const profileId = activeProfile?.id;
    if (!profileId) return;

    setCollectionLoading(true);
    setCollectionSelected(new Set());
    setCollectionSearch('');
    setPhase(PHASE.COLLECTION_PICK);

    try {
      const { data, error: fetchErr } = await supabase
        .from('cards')
        .select('*')
        .eq('user_id', profileId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (!fetchErr && data) {
        setCollectionCards(data);
      } else {
        setCollectionCards([]);
      }
    } catch (e) {
      console.error('[BulkScanner] Failed to fetch collection:', e);
      setCollectionCards([]);
    }
    setCollectionLoading(false);
  };

  const toggleCollectionCard = (cardId) => {
    setCollectionSelected(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        const limit = MAX_CARDS - cards.length;
        if (next.size >= limit) return prev;
        next.add(cardId);
      }
      return next;
    });
  };

  const addCollectionToTrade = () => {
    if (collectionSelected.size === 0) return;

    const selected = collectionCards.filter(c => collectionSelected.has(c.id));
    const newCards = selected.map(dbCard => {
      const candidate = {
        name: dbCard.name || 'Unknown Card',
        originalName: '',
        language: scanLang,
        setName: dbCard.set_name || '',
        setCode: '',
        number: dbCard.set_number || '',
        confidence: 100,
        image: dbCard.digital_image_url || dbCard.tcgdex_image || null,
        imageHigh: dbCard.digital_image_url || dbCard.tcgdex_image || null,
        tcgId: dbCard.card_id || null,
        source: 'collection',
        localId: dbCard.set_number || '',
        loading: false,
      };

      return {
        id: crypto.randomUUID(),
        croppedImage: dbCard.original_image_url || dbCard.digital_image_url || null,
        titanMatches: [],
        candidatesWithData: [candidate],
        selectedMatchIndex: 0,
        status: 'confirmed',
        error: null,
        variant: dbCard.variant || 'Normal',
        condition: dbCard.grade || 'NM',
        gradeType: dbCard.grade && dbCard.grade.includes(' ') ? 'graded' : 'raw',
        gradingCompany: dbCard.grade?.split(' ')[0] || 'PSA',
        tradingStatus: 'TRADE',
        binder: null,
        availableVariants: [dbCard.variant || 'Normal'],
        fromCollection: true,
      };
    });

    setCards(prev => [...prev, ...newCards]);

    // Cleanup
    setCollectionCards([]);
    setCollectionSelected(new Set());
    setCollectionSearch('');

    if (cards.length + newCards.length >= MAX_CARDS) {
      setPhase(PHASE.GALLERY);
    } else {
      setPhase(PHASE.CAPTURE);
    }
  };

  const filteredCollectionCards = collectionCards.filter(c => {
    if (!collectionSearch.trim()) return true;
    const q = collectionSearch.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.set_name || '').toLowerCase().includes(q) ||
      (c.set_number || '').toLowerCase().includes(q)
    );
  });

  // ==========================================
  // CARD FAN - MATCH SELECTION
  // ==========================================

  const openCardFan = (cardId) => {
    setFanCardId(cardId);
    setFanScrollIndex(0);
    setFanSearchMode(false);
    setFanSearchQuery('');
    setFanSearchResults([]);
    setPhase(PHASE.CARD_FAN);
  };

  const fanCard = cards.find(c => c.id === fanCardId);

  const confirmMatch = async (card, matchIndex) => {
    // Set selected match
    updateCard(card.id, { selectedMatchIndex: matchIndex, status: 'confirmed' });

    // Fetch variants from TCGdex
    const candidate = card.candidatesWithData[matchIndex];
    if (candidate?.tcgId) {
      try {
        const apiEndpoint = getTCGdexEndpoint('en');
        const res = await fetch(`${apiEndpoint}/cards/${candidate.tcgId}`);
        if (res.ok) {
          const details = await res.json();
          if (details.variants) {
            const variants = [];
            if (details.variants.normal) variants.push('Normal');
            if (details.variants.holo) variants.push('Holo');
            if (details.variants.reverse) variants.push('Reverse Holo');
            if (details.variants.firstEdition) variants.push('1st Edition');
            if (details.variants.wPromo) variants.push('W Promo');
            variants.push('Cosmos Holo', 'Cracked Ice Holo');
            updateCard(card.id, {
              availableVariants: variants,
              variant: variants[0] || 'Normal',
            });
          } else {
            updateCard(card.id, { availableVariants: ['Normal'], variant: 'Normal' });
          }
        }
      } catch (e) {
        console.log('[BulkScanner] Could not fetch variants:', e);
        updateCard(card.id, { availableVariants: ['Normal'], variant: 'Normal' });
      }
    } else {
      updateCard(card.id, {
        availableVariants: ['Normal', 'Holo', 'Reverse Holo', '1st Edition', 'W Promo', 'Cosmos Holo', 'Cracked Ice Holo'],
        variant: 'Normal',
      });
    }
  };

  const retakeCard = (cardId) => {
    const idx = cards.findIndex(c => c.id === cardId);
    if (idx !== -1) {
      setRetakeIndex(idx);
      setPhase(PHASE.CAPTURE);
    }
  };

  const removeCard = (cardId) => {
    setCards(prev => prev.filter(c => c.id !== cardId));
    setPhase(PHASE.GALLERY);
  };

  // Parse search query into name and optional card number
  const parseSearchQuery = (raw) => {
    const q = raw.trim();
    const numberPattern = /\s+((?:[a-zA-Z]{1,3})?\d+(?:\/\d+)?)$/;
    const match = q.match(numberPattern);
    if (match) {
      const numberRaw = match[1];
      const name = q.slice(0, match.index).trim();
      return { name: name || null, number: numberRaw.split('/')[0] };
    }
    if (/^(?:[a-zA-Z]{1,3})?\d+(?:\/\d+)?$/.test(q)) {
      return { name: null, number: q.split('/')[0] };
    }
    return { name: q, number: null };
  };

  const numberMatches = (cardNumber, searchNumber) => {
    if (!cardNumber || !searchNumber) return false;
    const normalize = (n) => n.toString().toLowerCase().replace(/^0+/, '') || '0';
    return normalize(cardNumber.split('/')[0]) === normalize(searchNumber);
  };

  const LANG_OPTIONS = [
    { code: 'en', label: 'English' },
    { code: 'ja', label: 'Japanese' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' },
    { code: 'es', label: 'Spanish' },
    { code: 'it', label: 'Italian' },
    { code: 'pt', label: 'Portuguese' },
    { code: 'ko', label: 'Korean' },
    { code: 'zh', label: 'Chinese' },
    { code: 'th', label: 'Thai' },
    { code: 'id', label: 'Indonesian' },
  ];

  const langName = (code) => LANG_OPTIONS.find(l => l.code === code)?.label || code;

  const runTranslation = async () => {
    if (!translateInput.trim()) return;
    setIsTranslating(true);
    setTranslateOutput('');
    try {
      const fromLang = langName(translateFrom);
      const toLang = langName(translateTo);
      const res = await fetch('https://api.x.ai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_GROK_API_KEY}`,
        },
        body: JSON.stringify({
          model: 'grok-3-mini',
          messages: [{
            role: 'user',
            content: `You are a Pokemon TCG card name translator. Translate this Pokemon card name from ${fromLang} to ${toLang}. This is specifically for searching the TCGdex card database. Reply with ONLY the translated card name, nothing else.\n\nCard name: ${translateInput.trim()}`
          }],
          temperature: 0,
        }),
      });
      if (!res.ok) throw new Error('Translation failed');
      const data = await res.json();
      const result = data.choices?.[0]?.message?.content?.trim();
      if (result) {
        setTranslateOutput(result);
        console.log(`[Translate] ${translateInput} (${fromLang}) -> ${result} (${toLang})`);
      } else {
        setTranslateOutput('Translation failed. Try again.');
      }
    } catch (e) {
      console.log('[Translate] Error:', e);
      setTranslateOutput('Translation failed. Try again.');
    }
    setIsTranslating(false);
  };

  const useTranslation = () => {
    if (translateOutput && !translateOutput.includes('failed')) {
      setFanSearchQuery(translateOutput);
      setShowTranslator(false);
    }
  };

  const swapLanguages = () => {
    setTranslateFrom(translateTo);
    setTranslateTo(translateFrom);
    setTranslateInput(translateOutput || '');
    setTranslateOutput(translateInput || '');
  };

  const fanSearchCards = async () => {
    if (!fanSearchQuery.trim()) return;

    setIsFanSearching(true);
    setFanSearchResults([]);

    try {
      let { name, number } = parseSearchQuery(fanSearchQuery);

      // Number-only search: hit local DB
      if (!name && number) {
        const localResults = searchLocalByNumber(number);
        if (localResults.length > 0) {
          setFanSearchResults(localResults);
        } else {
          setError(`No cards found with number ${number}.`);
        }
        setIsFanSearching(false);
        return;
      }

      if (!name || name.length < 3) {
        setError('Enter at least 3 characters for the card name.');
        setIsFanSearching(false);
        return;
      }

      // Search TCGdex in the selected scan language
      const apiEndpoint = getTCGdexEndpoint(scanLang);
      const response = await fetch(
        `${apiEndpoint}/cards?name=${encodeURIComponent(name)}`
      );

      if (!response.ok) throw new Error('Search failed');

      let cards = await response.json();

      // Pre-filter by number if provided
      if (number && cards.length > 0) {
        const filtered = cards.filter(c => {
          const cardNum = c.localId || c.id?.split('-').pop() || '';
          return numberMatches(cardNum, number);
        });
        if (filtered.length > 0) cards = filtered;
      }

      if (cards && cards.length > 0) {
        const allResults = [];
        for (let i = 0; i < cards.length; i += 10) {
          const batch = cards.slice(i, i + 10);
          const batchResults = await Promise.all(
            batch.map(async (card) => {
              try {
                const detailRes = await fetch(`${apiEndpoint}/cards/${card.id}`);
                if (detailRes.ok) {
                  const details = await detailRes.json();
                  return {
                    name: details.name,
                    setName: details.set?.name || 'Unknown Set',
                    setCode: details.set?.id || card.id?.split('-')[0] || '',
                    number: details.localId || '',
                    image: details.image ? `${details.image}/low.webp` : null,
                    imageHigh: details.image ? `${details.image}/high.webp` : null,
                    tcgId: card.id,
                    localId: details.localId || '',
                  };
                }
              } catch (e) {
                console.log('[BulkScanner] Failed to load card details:', card.id);
              }
              return {
                name: card.name,
                setName: card.id?.split('-')[0] || 'Unknown',
                setCode: card.id?.split('-')[0] || '',
                number: card.localId || '',
                image: card.image ? `${card.image}/low.webp` : null,
                imageHigh: card.image ? `${card.image}/high.webp` : null,
                tcgId: card.id,
                localId: card.localId || '',
              };
            })
          );
          allResults.push(...batchResults.filter(Boolean));
          setFanSearchResults([...allResults]);
        }

        // Final number filter on detailed results
        if (number) {
          const finalFiltered = allResults.filter(r => numberMatches(r.number, number));
          if (finalFiltered.length > 0) setFanSearchResults(finalFiltered);
        }
      } else {
        setError('No cards found. Try a different search.');
      }
    } catch (err) {
      console.error('[BulkScanner] Search error:', err);
      setError('Search failed. Please try again.');
    }

    setIsFanSearching(false);
  };

  const selectFanSearchResult = async (result) => {
    if (!fanCard) return;

    const candidate = {
      name: result.name,
      originalName: '',
      language: scanLang,
      setName: result.setName,
      setCode: result.setCode,
      number: result.number || result.localId,
      confidence: 100,
      image: result.image,
      imageHigh: result.imageHigh,
      tcgId: result.tcgId,
      source: 'manual_search',
      localId: result.localId || result.number,
      loading: false,
    };

    updateCard(fanCard.id, {
      candidatesWithData: [candidate],
      selectedMatchIndex: 0,
      status: 'confirmed',
    });

    // Fetch variants
    if (result.tcgId) {
      try {
        const apiEndpoint = getTCGdexEndpoint('en');
        const res = await fetch(`${apiEndpoint}/cards/${result.tcgId}`);
        if (res.ok) {
          const details = await res.json();
          if (details.variants) {
            const variants = [];
            if (details.variants.normal) variants.push('Normal');
            if (details.variants.holo) variants.push('Holo');
            if (details.variants.reverse) variants.push('Reverse Holo');
            if (details.variants.firstEdition) variants.push('1st Edition');
            if (details.variants.wPromo) variants.push('W Promo');
            variants.push('Cosmos Holo', 'Cracked Ice Holo');
            updateCard(fanCard.id, {
              availableVariants: variants,
              variant: variants[0] || 'Normal',
            });
          } else {
            updateCard(fanCard.id, { availableVariants: ['Normal'], variant: 'Normal' });
          }
        }
      } catch (e) {
        console.log('[BulkScanner] Could not fetch variants:', e);
        updateCard(fanCard.id, { availableVariants: ['Normal'], variant: 'Normal' });
      }
    } else {
      updateCard(fanCard.id, {
        availableVariants: ['Normal', 'Holo', 'Reverse Holo', '1st Edition', 'W Promo', 'Cosmos Holo', 'Cracked Ice Holo'],
        variant: 'Normal',
      });
    }

    // Reset search state and go back to gallery
    setFanSearchMode(false);
    setFanSearchQuery('');
    setFanSearchResults([]);
    setPhase(PHASE.GALLERY);
  };

  // ==========================================
  // DETAILS - SEQUENTIAL SAVE
  // ==========================================

  const startDetails = () => {
    // Auto-confirm any 'ready' cards with their top match (index 0)
    cards.forEach(card => {
      if (card.status === 'ready' && card.candidatesWithData?.length > 0) {
        const matchIdx = card.selectedMatchIndex ?? 0;
        confirmMatch(card, matchIdx);
      }
    });
    setPhase(PHASE.DETAILS);
  };

  const saveCurrentCard = async () => {
    if (requireAuth('save cards to your binder')) return;
    if (!user?.id || !currentDetailCard) return;

    setIsSaving(true);
    setError(null);

    const card = currentDetailCard;
    const match = card.candidatesWithData[card.selectedMatchIndex];

    try {
      // Upload scan image
      let uploadedImageUrl = null;
      if (card.croppedImage) {
        const blob = await fetch(card.croppedImage).then(r => r.blob());
        const filename = `${user.id}/${Date.now()}-bulk-scan.jpg`;
        const { error: uploadError } = await supabase.storage
          .from('Pokemon Card Images')
          .upload(filename, blob, { contentType: 'image/jpeg' });
        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from('Pokemon Card Images')
            .getPublicUrl(filename);
          uploadedImageUrl = urlData?.publicUrl;
        }
      }

      const cardId = crypto.randomUUID();
      const profileId = activeProfile?.id;
      if (!profileId) throw new Error('No active profile');

      const cardData = {
        id: cardId,
        user_id: profileId,
        binder_id: card.binder?.id || null,
        card_id: match?.tcgId || null,
        name: match?.name || 'Unknown Card',
        variant: card.variant,
        specification: card.specification || null,
        grade: card.condition,
        original_image_url: uploadedImageUrl,
        digital_image_url: match?.imageHigh || match?.image || null,
        tcgdex_image: match?.imageHigh || match?.image || null,
        set_name: match?.setName || '',
        set_number: match?.localId || match?.number || null,
        is_for_trade: isViewerOnly ? false : card.tradingStatus === 'TRADE',
        trading_status: isViewerOnly ? 'PC' : card.tradingStatus,
        traded: false,
        is_private: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { error: insertError } = await supabase.from('cards').insert(cardData);
      if (insertError) throw insertError;

      // Link to binder
      if (card.binder?.id) {
        await supabase.from('card_binders').insert({
          card_id: cardId,
          binder_id: card.binder.id,
        });
      }

      // Mark card as saved
      updateCard(card.id, { status: 'saved' });
      setSavedCount(prev => prev + 1);

      // Fetch and cache pricing for the newly saved card (non-blocking)
      fetchAndCacheSingleCard({
        name: cardData.name,
        setName: cardData.set_name || '',
        setNumber: cardData.set_number || '',
        variant: cardData.variant || '',
        grade: cardData.grade || 'NM',
      }).catch(() => {});

      // Check if any confirmed cards remain (after this one becomes 'saved')
      const remaining = cards.filter(c => c.status === 'confirmed' && c.id !== card.id);
      if (remaining.length === 0) {
        setPhase(PHASE.DONE);
      }
    } catch (err) {
      console.error('[BulkScanner] Save error:', err);
      setError(`Failed to save: ${err.message}`);
    }

    setIsSaving(false);
  };

  // ==========================================
  // TRADE MODE - Collect and navigate back
  // ==========================================

  const addCardsToTrade = async () => {
    // In trade mode, cards are marked 'saved' as user confirms each in details
    const confirmedCards = cards.filter(c => c.status === 'confirmed' || c.status === 'saved');
    const tradeCards = confirmedCards.map(c => {
      const match = c.candidatesWithData[c.selectedMatchIndex] || {};
      return {
        id: crypto.randomUUID(),
        side: side === 'theirs' ? 'theirs' : 'mine',
        name: match.name || 'Unknown Card',
        setName: match.setName || '',
        setCode: match.setCode || '',
        number: match.localId || match.number || '',
        tcgId: match.tcgId || null,
        userPhoto: c.croppedImage,
        digitalImage: match.imageHigh || match.image || null,
        variant: c.variant || 'Normal',
        condition: c.condition || 'NM',
        price: null,
        priceLoading: true,
        pricingData: null,
      };
    });

    await stopCamera();
    navigate('/fair-trade', { state: { tradeCards, tradeSide: side === 'theirs' ? 'theirs' : 'mine' } });
  };

  // ==========================================
  // QUICK SCAN - Strip memory & pricing
  // ==========================================

  const conditionToKey = (condition) => {
    if (!condition) return 'nm';
    const c = condition.toLowerCase();
    if (c === 'nm') return 'nm';
    if (c === 'lp') return 'lp';
    if (c === 'mp') return 'mp';
    if (c === 'hp') return 'hp';
    if (c === 'dmg') return 'dmg';
    return 'nm';
  };

  const stripCardMemory = (cardId) => {
    setCards(prev => prev.map(c => {
      if (c.id !== cardId) return c;
      const match = c.candidatesWithData?.[c.selectedMatchIndex] || {};
      return {
        id: c.id,
        name: match.name || 'Unknown Card',
        setName: match.setName || '',
        setCode: match.setCode || '',
        number: match.localId || match.number || '',
        tcgId: match.tcgId || null,
        digitalImage: match.imageHigh || match.image || null,
        variant: c.variant || 'Normal',
        condition: c.condition || 'NM',
        gradeType: c.gradeType || 'raw',
        gradingCompany: c.gradingCompany || 'PSA',
        // Null out heavy data
        croppedImage: null,
        candidatesWithData: null,
        titanMatches: null,
        availableVariants: null,
        selectedMatchIndex: null,
        status: 'saved',
      };
    }));
  };

  const startPricingPhase = async (savedCardsOverride) => {
    setPhase(PHASE.PRICE_RESULTS);
    setPricingProgress(0);

    // Use provided cards or read from state
    const savedCards = savedCardsOverride || cards.filter(c => c.status === 'saved');
    const initial = savedCards.map(c => ({
      id: c.id,
      name: c.name || 'Unknown Card',
      setName: c.setName || '',
      number: c.number || '',
      tcgId: c.tcgId || null,
      digitalImage: c.digitalImage || null,
      variant: c.variant || 'Normal',
      condition: c.condition || 'NM',
      price: null,
      priceLoading: true,
      pricingData: null,
    }));
    setPricingCards(initial);

    // Check cache first - prices may already be fetched during DETAILS phase
    const cacheCards = initial.map(c => ({
      id: c.id, name: c.name, set_name: c.setName,
      set_number: c.number, grade: c.condition, variant: c.variant,
    }));
    const cached = await getBulkCardPrices(cacheCards);

    // Apply cached prices immediately
    const stillMissing = [];
    const cacheResults = initial.map(card => {
      const hit = cached.get(card.id);
      if (hit?.price != null) {
        return { id: card.id, price: hit.price, pricingData: hit.row, priceLoading: false };
      }
      stillMissing.push(card);
      return null;
    }).filter(Boolean);

    if (cacheResults.length > 0) {
      setPricingCards(prev => prev.map(pc => {
        const result = cacheResults.find(r => r.id === pc.id);
        return result ? { ...pc, ...result } : pc;
      }));
      setPricingProgress(cacheResults.length);
    }

    // Fetch missing prices via Countdown (batches of 5)
    let completed = cacheResults.length;
    for (let i = 0; i < stillMissing.length; i += 5) {
      const batch = stillMissing.slice(i, i + 5);
      const results = await Promise.all(
        batch.map(async (card) => {
          try {
            await fetchAndCacheSingleCard({
              name: card.name,
              setName: card.setName,
              setNumber: card.number,
              variant: card.variant,
              grade: card.condition,
            });
            // Re-read from cache to get computed price
            const row = await getBulkCardPrices([{
              id: card.id, name: card.name, set_name: card.setName,
              set_number: card.number, grade: card.condition, variant: card.variant,
            }]);
            const hit = row.get(card.id);
            return { id: card.id, price: hit?.price ?? null, pricingData: hit?.row ?? null, priceLoading: false };
          } catch (err) {
            console.error(`[BulkScanner] Pricing error for ${card.name}:`, err);
            return { id: card.id, price: null, pricingData: null, priceLoading: false };
          }
        })
      );

      setPricingCards(prev => prev.map(pc => {
        const result = results.find(r => r.id === pc.id);
        return result ? { ...pc, ...result } : pc;
      }));

      completed += batch.length;
      setPricingProgress(completed);
    }
  };

  const handleExportPdf = async () => {
    setIsExportingPdf(true);
    try {
      await generatePriceReportPdf(pricingCards);
    } catch (err) {
      console.error('[BulkScanner] Export error:', err);
    }
    setIsExportingPdf(false);
  };

  // ==========================================
  // NAVIGATION
  // ==========================================

  const handleClose = async () => {
    if (hasUnsavedCards) {
      setShowExitConfirm(true);
    } else {
      await stopCamera();
      navigate(-1);
    }
  };

  const confirmExit = async () => {
    setShowExitConfirm(false);
    await stopCamera();
    navigate(-1);
  };

  // ==========================================
  // RENDER
  // ==========================================

  // Gallery "Next" enabled when all cards are identified (ready/confirmed) -- no tap needed if matches look right
  const allIdentified = cards.length > 0 && cards.every(c => c.status === 'ready' || c.status === 'confirmed' || c.status === 'saved');
  const hasUnsavedCards = cards.some(c => c.status !== 'saved' && c.status !== 'error');

  return (
    <div style={styles.page}>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <canvas ref={cropCanvasRef} style={{ display: 'none' }} />

      {/* Camera container - always in DOM */}
      <div
        id="bulk-camera-view"
        style={{
          ...styles.cameraArea,
          visibility: phase === PHASE.CAPTURE ? 'visible' : 'hidden',
          pointerEvents: phase === PHASE.CAPTURE ? 'auto' : 'none',
        }}
      >
        {!Capacitor.isNativePlatform() && (
          <video ref={videoRef} style={styles.video} playsInline autoPlay muted />
        )}
      </div>

      {/* ========== CAPTURE PHASE ========== */}
      {phase === PHASE.CAPTURE && (
        <>
          {/* Loading overlay */}
          {!cameraReady && !cameraFailed && (
            <div style={styles.loadingOverlay}>
              <div style={styles.spinner} />
            </div>
          )}

          {/* Camera failed */}
          {cameraFailed && (
            <div style={styles.loadingOverlay}>
              <div style={{ textAlign: 'center', padding: 24 }}>
                <h2 style={{ color: '#fff', fontSize: 20, margin: '0 0 8px' }}>Camera Unavailable</h2>
                <p style={{ color: '#888', fontSize: 14, margin: '0 0 24px' }}>
                  Camera access is required for bulk scanning.
                </p>
                <button onClick={pickFromLibrary} style={{ ...styles.confirmBtn, marginBottom: 12 }}>
                  Choose from Photos
                </button>
                <button onClick={() => navigate(-1)} style={styles.outlineBtn}>
                  Go Back
                </button>
              </div>
            </div>
          )}

          {/* Frame overlay with detection */}
          {(() => {
            const { detected, edgeCount, coverage } = cardFill;
            const partial = edgeCount >= 2 && !detected;
            const edgeColor = (active) => active ? '#22c55e' : 'rgba(255,255,255,0.3)';
            const cornerColor = detected ? '#22c55e' : partial ? '#f59e0b' : 'rgba(255,255,255,0.65)';
            const cornerSize = detected ? 32 : partial ? 26 : 24;
            const cornerWidth = detected ? 4.5 : partial ? 4 : 3.5;
            const headerText = detected ? 'Card detected' : partial ? 'Almost there' : (retakeIndex !== null ? 'Retake' : 'Scan your card');
            const subtitleText = detected ? 'Tap the button to capture' : partial ? 'Move card to fill the frame' : 'Position it inside the guide';
            const headerColor = detected ? '#22c55e' : partial ? '#f59e0b' : '#fff';
            const btnDisabled = !cameraReady || isCapturing || (cards.length >= MAX_CARDS && retakeIndex === null);
            const btnBorderColor = detected ? '#22c55e' : partial ? 'rgba(234,179,8,0.6)' : 'rgba(255,255,255,0.5)';
            const btnFillColor = detected ? '#22c55e' : '#fff';
            const vignetteAlpha = detected ? 0.45 : partial ? 0.38 : 0.3;

            // Progress segment: 3-state (gray / amber / green+pulse)
            const segColor = (edge) => {
              if (detected) return '#22c55e';
              if (coverage[edge]) return 'rgba(234,179,8,0.7)';
              return 'rgba(255,255,255,0.12)';
            };

            return (
              <>
                {/* Dynamic vignette overlay */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  background: `radial-gradient(ellipse 70% 55% at 50% 45%, transparent 0%, rgba(0,0,0,${vignetteAlpha}) 100%)`,
                  pointerEvents: 'none',
                  transition: 'background 0.4s ease',
                }} />

                {/* Flash overlay */}
                {showFlash && (
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    zIndex: 100,
                    animation: 'captureFlash 0.15s ease-out forwards',
                    pointerEvents: 'none',
                  }} />
                )}

                {/* Frame + edges */}
                <div
                  style={styles.frameOverlay}
                  onTouchEnd={(e) => {
                    if (!Capacitor.isNativePlatform() || !cameraReady) return;
                    const touch = e.changedTouches[0];
                    CameraService.focusAtPoint(touch.clientX, touch.clientY);
                  }}
                >
                  <div ref={frameRef} style={{
                    width: '75%',
                    maxHeight: '60%',
                    aspectRatio: '5/7',
                    position: 'relative',
                    borderRadius: 12,
                    boxShadow: detected
                      ? '0 0 30px 6px rgba(34,197,94,0.35), inset 0 0 20px 2px rgba(34,197,94,0.12)'
                      : partial
                        ? '0 0 12px 2px rgba(245,158,11,0.15)'
                        : 'none',
                    transition: 'box-shadow 0.4s ease',
                  }}>
                    {/* Per-edge borders */}
                    {/* Top */}
                    <div style={{
                      position: 'absolute', top: 0, left: 12, right: 12,
                      height: coverage.top ? 2.5 : 1,
                      backgroundColor: edgeColor(coverage.top),
                      borderRadius: '3px 3px 0 0',
                      transition: 'background-color 0.3s ease, height 0.3s ease',
                    }} />
                    {/* Bottom */}
                    <div style={{
                      position: 'absolute', bottom: 0, left: 12, right: 12,
                      height: coverage.bottom ? 2.5 : 1,
                      backgroundColor: edgeColor(coverage.bottom),
                      borderRadius: '0 0 3px 3px',
                      transition: 'background-color 0.3s ease, height 0.3s ease',
                    }} />
                    {/* Left */}
                    <div style={{
                      position: 'absolute', top: 12, bottom: 12, left: 0,
                      width: coverage.left ? 2.5 : 1,
                      backgroundColor: edgeColor(coverage.left),
                      borderRadius: '3px 0 0 3px',
                      transition: 'background-color 0.3s ease, width 0.3s ease',
                    }} />
                    {/* Right */}
                    <div style={{
                      position: 'absolute', top: 12, bottom: 12, right: 0,
                      width: coverage.right ? 2.5 : 1,
                      backgroundColor: edgeColor(coverage.right),
                      borderRadius: '0 3px 3px 0',
                      transition: 'background-color 0.3s ease, width 0.3s ease',
                    }} />

                    {/* Corner brackets */}
                    {/* Top-left */}
                    <div style={{
                      position: 'absolute', top: -1, left: -1,
                      width: cornerSize, height: cornerSize,
                      borderLeft: `${cornerWidth}px solid ${cornerColor}`,
                      borderTop: `${cornerWidth}px solid ${cornerColor}`,
                      borderRadius: '4px 0 0 0',
                      transition: 'all 0.3s ease',
                    }} />
                    {/* Top-right */}
                    <div style={{
                      position: 'absolute', top: -1, right: -1,
                      width: cornerSize, height: cornerSize,
                      borderRight: `${cornerWidth}px solid ${cornerColor}`,
                      borderTop: `${cornerWidth}px solid ${cornerColor}`,
                      borderRadius: '0 4px 0 0',
                      transition: 'all 0.3s ease',
                    }} />
                    {/* Bottom-left */}
                    <div style={{
                      position: 'absolute', bottom: -1, left: -1,
                      width: cornerSize, height: cornerSize,
                      borderLeft: `${cornerWidth}px solid ${cornerColor}`,
                      borderBottom: `${cornerWidth}px solid ${cornerColor}`,
                      borderRadius: '0 0 0 4px',
                      transition: 'all 0.3s ease',
                    }} />
                    {/* Bottom-right */}
                    <div style={{
                      position: 'absolute', bottom: -1, right: -1,
                      width: cornerSize, height: cornerSize,
                      borderRight: `${cornerWidth}px solid ${cornerColor}`,
                      borderBottom: `${cornerWidth}px solid ${cornerColor}`,
                      borderRadius: '0 0 4px 0',
                      transition: 'all 0.3s ease',
                    }} />

                    {/* Slab boundary indicator */}
                    {scanType === 'slab' && (
                      <>
                        <div style={{
                          position: 'absolute',
                          top: '-40%',
                          left: '-12%',
                          width: '124%',
                          height: '155%',
                          border: '1.5px dashed rgba(255,255,255,0.2)',
                          borderRadius: 8,
                          pointerEvents: 'none',
                        }} />
                        <div style={{
                          position: 'absolute',
                          top: '-30%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          color: 'rgba(255,255,255,0.3)',
                          fontSize: 10,
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          pointerEvents: 'none',
                        }}>
                          Grade label area
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Top bar */}
                <div style={styles.topBar}>
                  {/* Row 1: Close, status, badge */}
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <button style={styles.iconBtn} onClick={handleClose}>
                      <CloseIcon />
                    </button>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                      <span style={{
                        color: headerColor,
                        fontSize: 16,
                        fontWeight: 700,
                        letterSpacing: 0.2,
                        textShadow: '0 1px 6px rgba(0,0,0,0.6)',
                        transition: 'color 0.3s ease',
                      }}>
                        {headerText}
                      </span>
                      <span style={{
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: 11,
                        fontWeight: 400,
                        textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                      }}>
                        {subtitleText}
                      </span>
                    </div>
                    {cards.length > 0 && retakeIndex === null && (
                      <button ref={badgeRef} style={styles.badgeBtn} onClick={() => setPhase(PHASE.GALLERY)}>
                        <span style={styles.badgeText}>{cards.length}/{MAX_CARDS}</span>
                      </button>
                    )}
                    {cards.length > 0 && retakeIndex !== null && (
                      <button ref={badgeRef} style={{ ...styles.badgeBtn, backgroundColor: 'rgba(239, 147, 55, 0.9)' }} onClick={() => { setRetakeIndex(null); setPhase(PHASE.GALLERY); }}>
                        <span style={styles.badgeText}>Retake</span>
                      </button>
                    )}
                    {cards.length === 0 && <div ref={badgeRef} style={{ width: 44 }} />}
                  </div>

                  {/* Row 2: Progress bar + selectors */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    width: '100%', marginTop: 14,
                    padding: '0 2px',
                  }}>
                    <select
                      value={scanType}
                      onChange={(e) => setScanType(e.target.value)}
                      style={{
                        padding: '6px 24px 6px 10px',
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: 0.3,
                        textTransform: 'uppercase',
                        borderRadius: 20,
                        border: '1px solid rgba(255,255,255,0.12)',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        color: '#fff',
                        cursor: 'pointer',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'8\' height=\'5\'%3E%3Cpath d=\'M0 0l4 5 4-5z\' fill=\'rgba(255,255,255,0.5)\'/%3E%3C/svg%3E")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 8px center',
                      }}
                    >
                      <option value="raw">Raw</option>
                      <option value="slab">Slab</option>
                    </select>

                    <div style={{ flex: 1, display: 'flex', gap: 3, padding: '0 8px' }}>
                      {['top', 'right', 'bottom', 'left'].map((edge) => (
                        <div key={edge} style={{
                          flex: 1, height: 3, borderRadius: 2,
                          backgroundColor: segColor(edge),
                          transition: 'background-color 0.3s ease',
                          animation: detected && coverage[edge] ? 'segPulse 1.5s ease-in-out infinite' : 'none',
                        }} />
                      ))}
                    </div>

                    <select
                      value={scanLang}
                      onChange={(e) => setScanLang(e.target.value)}
                      style={{
                        padding: '6px 24px 6px 10px',
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: 0.3,
                        textTransform: 'uppercase',
                        borderRadius: 20,
                        border: '1px solid rgba(255,255,255,0.12)',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        color: '#fff',
                        cursor: 'pointer',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'8\' height=\'5\'%3E%3Cpath d=\'M0 0l4 5 4-5z\' fill=\'rgba(255,255,255,0.5)\'/%3E%3C/svg%3E")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 8px center',
                      }}
                    >
                      {[
                        { code: 'en', short: 'EN', full: 'English' },
                        { code: 'ja', short: 'JP', full: 'Japanese' },
                        { code: 'fr', short: 'FR', full: 'French' },
                        { code: 'de', short: 'DE', full: 'German' },
                        { code: 'es', short: 'ES', full: 'Spanish' },
                        { code: 'it', short: 'IT', full: 'Italian' },
                        { code: 'pt', short: 'PT', full: 'Portuguese' },
                        { code: 'zh-tw', short: 'ZH', full: 'Chinese' },
                        { code: 'th', short: 'TH', full: 'Thai' },
                        { code: 'id', short: 'ID', full: 'Indonesian' },
                      ].map(({ code, short, full }) => (
                        <option key={code} value={code}>{short} - {full}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Bottom bar */}
                <div style={{ ...styles.bottomBar, justifyContent: 'space-between' }}>
                  <button
                    style={styles.photosBtn}
                    onClick={pickFromLibrary}
                    disabled={cards.length >= MAX_CARDS && retakeIndex === null}
                  >
                    <PhotosIcon />
                    <span style={{ color: '#fff', fontSize: 11, fontWeight: 500, marginTop: 2 }}>Photos</span>
                  </button>
                  <button
                    style={{
                      width: 72, height: 72, borderRadius: 36,
                      border: `4px solid ${btnBorderColor}`,
                      backgroundColor: 'transparent',
                      padding: 4,
                      opacity: btnDisabled ? 0.5 : 1,
                      boxShadow: detected
                        ? '0 0 16px 2px rgba(34,197,94,0.4), inset 0 0 4px rgba(34,197,94,0.2)'
                        : 'none',
                      transition: 'border 0.3s ease, box-shadow 0.3s ease, opacity 0.2s ease',
                    }}
                    onClick={capturePhoto}
                    disabled={btnDisabled}
                  >
                    <div style={{
                      width: '100%', height: '100%', borderRadius: '50%',
                      backgroundColor: btnFillColor,
                      transition: 'background-color 0.3s ease',
                    }} />
                  </button>
                  {isTradeMode ? (
                    <button
                      style={styles.photosBtn}
                      onClick={openCollectionPicker}
                      disabled={cards.length >= MAX_CARDS && retakeIndex === null}
                    >
                      <CollectionIcon />
                      <span style={{ color: '#fff', fontSize: 11, fontWeight: 500, marginTop: 2 }}>My Cards</span>
                    </button>
                  ) : (
                    <div style={{ width: 80 }} />
                  )}
                </div>
              </>
            );
          })()}

          {/* Flying card animation */}
          <AnimatePresence>
            {flyingCard && (() => {
              const startRect = flyingCard.startRect;
              // Always target top-right where badge lives/will appear
              const targetX = window.innerWidth - 50;
              const targetY = 40;
              const deltaX = targetX - startRect.left;
              const deltaY = targetY - startRect.top;
              const targetScale = 0.08;

              return (
                <motion.div
                  key="flying-card"
                  initial={{
                    x: startRect.left,
                    y: startRect.top,
                    width: startRect.width,
                    height: startRect.height,
                    opacity: 1,
                    scale: 1,
                    borderRadius: 8,
                  }}
                  animate={{
                    x: startRect.left + deltaX,
                    y: startRect.top + deltaY,
                    scale: targetScale,
                    opacity: 0.6,
                    borderRadius: 20,
                  }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: 0.4,
                    ease: [0.4, 0, 0.2, 1],
                  }}
                  onAnimationComplete={onFlyComplete}
                  style={{
                    position: 'fixed',
                    zIndex: 10000,
                    pointerEvents: 'none',
                    transformOrigin: 'top left',
                    overflow: 'hidden',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  }}
                >
                  <img
                    src={flyingCard.image}
                    alt=""
                    style={{
                      width: startRect.width,
                      height: startRect.height,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </>
      )}

      {/* ========== LIBRARY CROP PHASE ========== */}
      {phase === PHASE.LIBRARY_CROP && libraryPhotos.length > 0 && (
        <div style={styles.libraryCropPage}>
          {/* Top bar */}
          <div style={styles.libraryCropTop}>
            <button style={styles.iconBtn} onClick={goBackCrop}>
              <CloseIcon />
            </button>
            <span style={styles.libraryCropProgress}>
              {libraryCropIndex + 1} of {libraryPhotos.length}
            </span>
            <div style={{ width: 44 }} />
          </div>

          {/* Crop area */}
          <div style={styles.libraryCropArea}>
            <TransformWrapper
              ref={transformRef}
              initialScale={1}
              minScale={0.5}
              maxScale={4}
              centerOnInit
              wheel={{ step: 0.1 }}
              pinch={{ step: 5 }}
            >
              <TransformComponent
                wrapperStyle={{ width: '100%', height: '100%' }}
                contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <img
                  ref={pickCropImgRef}
                  key={libraryPhotos[libraryCropIndex]}
                  src={libraryPhotos[libraryCropIndex]}
                  alt=""
                  style={styles.libraryCropImg}
                  crossOrigin="anonymous"
                  onError={() => skipCurrentPhoto()}
                />
              </TransformComponent>
            </TransformWrapper>

            {/* Frame overlay (pointer-events: none so zoom/pan works through it) */}
            <div style={styles.libraryCropFrameOverlay}>
              <div ref={pickCropFrameRef} style={{ ...styles.frame, aspectRatio: '5/7' }}>
                <div style={{ ...styles.corner, top: 0, left: 0 }} />
                <div style={{ ...styles.corner, top: 0, right: 0, transform: 'rotate(90deg)' }} />
                <div style={{ ...styles.corner, bottom: 0, left: 0, transform: 'rotate(-90deg)' }} />
                <div style={{ ...styles.corner, bottom: 0, right: 0, transform: 'rotate(180deg)' }} />
              </div>
              <p style={{ ...styles.hint, marginTop: 12 }}>Position card in frame</p>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, margin: '4px 0 0' }}>Pinch to zoom, drag to move</p>
            </div>
          </div>

          {/* Bottom bar */}
          <div style={styles.libraryCropBottom}>
            <button style={styles.libraryCropBackBtn} onClick={goBackCrop}>
              <BackIcon />
              <span>Back</span>
            </button>
            <button style={styles.libraryCropSkipBtn} onClick={skipCurrentPhoto}>
              Skip
            </button>
            <button style={styles.libraryCropCropBtn} onClick={cropCurrentPhoto}>
              {libraryCropIndex === libraryPhotos.length - 1 ? 'Crop & Done' : 'Crop & Next'}
            </button>
          </div>
        </div>
      )}

      {/* ========== COLLECTION PICK PHASE (Trade mode) ========== */}
      {phase === PHASE.COLLECTION_PICK && (
        <div style={styles.collectionPickPage}>
          <div style={styles.collectionPickHeader}>
            <button style={{ ...styles.iconBtn, backgroundColor: '#1a1a1a' }} onClick={() => { setCollectionCards([]); setCollectionSelected(new Set()); setPhase(PHASE.CAPTURE); }}>
              <BackIcon />
            </button>
            <span style={styles.headerTitle}>My Collection</span>
            <button
              style={{
                ...styles.nextBtn,
                opacity: collectionSelected.size > 0 ? 1 : 0.4,
              }}
              disabled={collectionSelected.size === 0}
              onClick={addCollectionToTrade}
            >
              Add ({collectionSelected.size})
            </button>
          </div>

          <div style={styles.collectionSearchBar}>
            <SearchIcon size={18} />
            <input
              type="text"
              value={collectionSearch}
              onChange={(e) => setCollectionSearch(e.target.value)}
              placeholder="Search your cards..."
              style={styles.collectionSearchInput}
            />
          </div>

          {collectionLoading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={styles.spinner} />
            </div>
          ) : filteredCollectionCards.length === 0 ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, padding: 40 }}>
              <span style={{ color: '#666', fontSize: 15 }}>
                {collectionSearch ? 'No cards match your search' : 'No cards in your collection'}
              </span>
            </div>
          ) : (
            <div style={styles.collectionGrid}>
              {filteredCollectionCards.map(card => {
                const isSelected = collectionSelected.has(card.id);
                const imgSrc = card.digital_image_url || card.tcgdex_image || card.original_image_url;
                return (
                  <button
                    key={card.id}
                    style={{
                      ...styles.collectionCard,
                      ...(isSelected ? styles.collectionCardSelected : {}),
                    }}
                    onClick={() => toggleCollectionCard(card.id)}
                  >
                    {imgSrc ? (
                      <img src={imgSrc} alt={card.name} style={styles.collectionCardImg} loading="lazy" />
                    ) : (
                      <div style={styles.collectionCardNoImg}><NoImageIcon /></div>
                    )}
                    {isSelected && (
                      <div style={styles.collectionCardCheck}>
                        <CheckIcon size={16} />
                      </div>
                    )}
                    <span style={styles.collectionCardName}>{card.name || 'Unknown'}</span>
                    <span style={styles.collectionCardSet}>{card.set_name || ''}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ========== GALLERY PHASE - Vertical list with scan | fan | info ========== */}
      {phase === PHASE.GALLERY && (
        <div style={styles.galleryPage}>
          <div style={styles.galleryHeader}>
            <button style={{ ...styles.iconBtn, backgroundColor: '#1a1a1a' }} onClick={() => setPhase(PHASE.CAPTURE)}>
              <BackIcon />
            </button>
            <span style={styles.headerTitle}>Review Matches</span>
            <button
              style={{
                ...styles.nextBtn,
                opacity: allIdentified ? 1 : 0.4,
              }}
              disabled={!allIdentified}
              onClick={startDetails}
            >
              Next
            </button>
          </div>

          <div style={styles.galleryList}>
            {cards.map((card, rowIdx) => {
              const topMatch = card.candidatesWithData[card.selectedMatchIndex ?? 0];
              const isSaved = card.status === 'saved';

              return (
                <div
                  key={card.id}
                  style={{
                    ...styles.galleryRow,
                    ...(isSaved ? { opacity: 0.35, pointerEvents: 'none' } : {}),
                  }}
                >
                  {/* Saved overlay */}
                  {isSaved && (
                    <div style={styles.savedOverlay}>
                      <CheckIcon size={16} />
                      <span>Added to collection</span>
                    </div>
                  )}

                  {/* Col 1: My scan */}
                  <div style={styles.rowScanWrap}>
                    <img src={card.croppedImage} alt="" style={styles.rowScanImg} />
                  </div>

                  {/* Col 2: Fan of match candidates */}
                  <button
                    style={styles.rowFanWrap}
                    onClick={() => {
                      if (!isSaved && card.status !== 'identifying') {
                        openCardFan(card.id);
                      }
                    }}
                  >
                    {card.status === 'identifying' ? (
                      <div style={styles.rowFanPlaceholder}>
                        <div style={styles.spinnerSmall} />
                      </div>
                    ) : card.status === 'error' || !card.candidatesWithData?.length ? (
                      <div style={styles.rowFanPlaceholder}>
                        <ExclamationIcon size={16} />
                      </div>
                    ) : (
                      <div style={styles.rowFanStack}>
                        {/* Behind cards (tilted) - show non-selected candidates */}
                        {(() => {
                          const selIdx = card.selectedMatchIndex ?? 0;
                          const others = card.candidatesWithData.filter((_, i) => i !== selIdx).slice(0, 2).reverse();
                          return others.map((cand, idx) => {
                            const tilt = (others.length - idx) * 8;
                            return (
                              <motion.div
                                key={`bg-${idx}`}
                                initial={false}
                                animate={{ rotate: tilt }}
                                style={{
                                  ...styles.rowFanCardBg,
                                  zIndex: idx,
                                  marginRight: -36,
                                }}
                              >
                                {(cand.imageHigh || cand.image) ? (
                                  <img src={cand.imageHigh || cand.image} alt="" style={styles.rowFanCardImg} />
                                ) : (
                                  <div style={styles.rowFanCardEmpty}><NoImageIcon /></div>
                                )}
                              </motion.div>
                            );
                          });
                        })()}
                        {/* Front card (upright, highlighted) - show selected match */}
                        {card.candidatesWithData[card.selectedMatchIndex ?? 0] && (
                          <div style={styles.rowFanCardFront}>
                            {(card.candidatesWithData[card.selectedMatchIndex ?? 0].imageHigh || card.candidatesWithData[card.selectedMatchIndex ?? 0].image) ? (
                              <img src={card.candidatesWithData[card.selectedMatchIndex ?? 0].imageHigh || card.candidatesWithData[card.selectedMatchIndex ?? 0].image} alt="" style={styles.rowFanCardImg} />
                            ) : (
                              <div style={styles.rowFanCardEmpty}><NoImageIcon /></div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </button>

                  {/* Col 3: Card info + change hint */}
                  <div style={styles.rowInfo}>
                    {card.status === 'identifying' ? (
                      <span style={styles.rowInfoName}>Identifying...</span>
                    ) : card.status === 'error' ? (
                      <>
                        <span style={{ ...styles.rowInfoName, color: '#ef4444' }}>No match</span>
                        <span
                          style={styles.rowChangeHint}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isSaved) openCardFan(card.id);
                          }}
                        >
                          Search manually
                        </span>
                      </>
                    ) : topMatch ? (
                      <>
                        <span style={styles.rowInfoName}>{topMatch.name}</span>
                        <span style={styles.rowInfoSet}>{topMatch.setName}</span>
                        <span style={styles.rowInfoNumber}>#{topMatch.number}</span>
                        {card.candidatesWithData?.length > 1 && (
                          <span
                            style={styles.rowChangeHint}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isSaved) openCardFan(card.id);
                            }}
                          >
                            Tap to change
                          </span>
                        )}
                      </>
                    ) : (
                      <span style={styles.rowInfoName}>Unknown</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========== CARD FAN PHASE - Alternate match picker ========== */}
      {phase === PHASE.CARD_FAN && fanCard && (
        <div style={styles.fanPage}>
          <div style={styles.fanHeader}>
            <button style={{ ...styles.iconBtn, backgroundColor: '#1a1a1a' }} onClick={() => setPhase(PHASE.GALLERY)}>
              <BackIcon />
            </button>
            <span style={styles.headerTitle}>{fanSearchMode ? 'Search Card' : 'Select Match'}</span>
            <div style={{ width: 44 }} />
          </div>

          <div style={styles.fanScanWrap}>
            <span style={styles.fanScanLabel}>Your Scan</span>
            <img src={fanCard.croppedImage} alt="Scan" style={styles.fanScanImg} />
          </div>

          {fanSearchMode ? (
            <>
              <div style={styles.fanSearchBar}>
                <input
                  type="text"
                  value={fanSearchQuery}
                  onChange={(e) => setFanSearchQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fanSearchCards(); }}
                  placeholder="Name, number, or both..."
                  style={styles.fanSearchInput}
                  autoFocus
                />
                <button
                  style={styles.fanSearchBtn}
                  onClick={fanSearchCards}
                  disabled={isFanSearching}
                >
                  {isFanSearching ? <div style={styles.spinnerSmall} /> : <SearchIcon />}
                </button>
              </div>

              {/* Translate button */}
              <button
                style={styles.translateToggleBtn}
                onClick={() => setShowTranslator(!showTranslator)}
              >
                <span style={{ fontSize: 14 }}>&#127760;</span>
                <span>{showTranslator ? 'Hide Translator' : 'Translate Card Name'}</span>
              </button>

              {/* Translator panel */}
              {showTranslator && (
                <div style={styles.translatorPanel}>
                  <div style={styles.translatorLangRow}>
                    <select
                      value={translateFrom}
                      onChange={(e) => setTranslateFrom(e.target.value)}
                      style={styles.translatorSelect}
                    >
                      {LANG_OPTIONS.map(l => (
                        <option key={l.code} value={l.code}>{l.label}</option>
                      ))}
                    </select>

                    <button style={styles.translatorSwapBtn} onClick={swapLanguages}>
                      &#8644;
                    </button>

                    <select
                      value={translateTo}
                      onChange={(e) => setTranslateTo(e.target.value)}
                      style={styles.translatorSelect}
                    >
                      {LANG_OPTIONS.map(l => (
                        <option key={l.code} value={l.code}>{l.label}</option>
                      ))}
                    </select>
                  </div>

                  <input
                    type="text"
                    value={translateInput}
                    onChange={(e) => setTranslateInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') runTranslation(); }}
                    placeholder={`Type card name in ${langName(translateFrom)}...`}
                    style={styles.translatorInput}
                  />

                  <button
                    style={styles.translatorGoBtn}
                    onClick={runTranslation}
                    disabled={isTranslating || !translateInput.trim()}
                  >
                    {isTranslating ? 'Translating...' : 'Translate'}
                  </button>

                  {translateOutput && (
                    <div style={styles.translatorResultBox}>
                      <span style={styles.translatorResultLabel}>{langName(translateTo)} name:</span>
                      <span style={styles.translatorResultText}>{translateOutput}</span>

                      {!translateOutput.includes('failed') && (
                        <button style={styles.translatorUseBtn} onClick={useTranslation}>
                          Use This
                        </button>
                      )}

                      <button
                        style={styles.translatorRetryBtn}
                        onClick={() => { setTranslateOutput(''); setTranslateInput(''); }}
                      >
                        Try Something Else
                      </button>
                    </div>
                  )}

                  <p style={styles.translatorNote}>
                    Note: JP and other non-English cards may not use card numbers for lookup.
                    Try searching by name only for best results.
                  </p>
                </div>
              )}

              <div style={styles.fanSearchResultsList}>
                {fanSearchResults.map((result, idx) => (
                  <div key={idx} style={styles.fanSearchResultRow}>
                    {result.image ? (
                      <img src={result.image} alt="" style={styles.fanSearchResultImg} />
                    ) : (
                      <div style={{ ...styles.fanSearchResultImg, backgroundColor: '#252525', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <NoImageIcon />
                      </div>
                    )}
                    <div style={styles.fanSearchResultInfo}>
                      <span style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{result.name}</span>
                      <span style={{ color: '#888', fontSize: 12 }}>{result.setName}</span>
                      <span style={{ color: '#666', fontSize: 11 }}>#{result.number}</span>
                    </div>
                    <button
                      style={styles.fanSearchSelectBtn}
                      onClick={() => selectFanSearchResult(result)}
                    >
                      Select
                    </button>
                  </div>
                ))}
                {isFanSearching && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                    <div style={styles.spinner} />
                  </div>
                )}
              </div>

              {/* Still can't find */}
              <button
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  backgroundColor: 'transparent',
                  border: '1px solid #f59e0b',
                  borderRadius: 10,
                  color: '#f59e0b',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginTop: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
                onClick={() => {
                  setContributeCardId(fanCard.id);
                  setShowContributePrompt(true);
                  setShowManualEntry(false);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Still can't find your card?
              </button>

              {fanCard.candidatesWithData?.length > 0 && (
                <button
                  style={{ ...styles.removeBtn, textAlign: 'center', padding: '12px 20px', marginTop: 8 }}
                  onClick={() => setFanSearchMode(false)}
                >
                  Back to matches
                </button>
              )}
            </>
          ) : (
            <>
              {fanCard.candidatesWithData?.length > 0 ? (
                <>
                  <span style={styles.fanMatchLabel}>Tap the correct match</span>
                  <div style={styles.fanScroll} ref={fanScrollRef}>
                    {fanCard.candidatesWithData.map((candidate, idx) => (
                      <button
                        key={idx}
                        style={{
                          ...styles.fanCandidate,
                          ...((fanCard.selectedMatchIndex ?? 0) === idx ? styles.fanCandidateActive : {}),
                        }}
                        onClick={() => {
                          confirmMatch(fanCard, idx);
                          setPhase(PHASE.GALLERY);
                        }}
                      >
                        {candidate.loading ? (
                          <div style={styles.fanCandidateImgPlaceholder}>
                            <div style={styles.spinnerSmall} />
                          </div>
                        ) : candidate.image || candidate.imageHigh ? (
                          <img src={candidate.imageHigh || candidate.image} alt={candidate.name} style={styles.fanCandidateImg} />
                        ) : (
                          <div style={styles.fanCandidateImgPlaceholder}><NoImageIcon /></div>
                        )}
                        <span style={styles.fanCandidateName}>{candidate.name}</span>
                        <span style={styles.fanCandidateSet}>{candidate.setName}</span>
                        <span style={styles.fanCandidateNumber}>#{candidate.number}</span>
                        <span style={styles.fanCandidateConf}>{candidate.confidence}%</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, padding: 20 }}>
                  <ExclamationIcon size={32} />
                  <span style={{ color: '#999', fontSize: 15, textAlign: 'center' }}>
                    {fanCard.status === 'error' ? (fanCard.error || 'No matches found') : 'No matches available'}
                  </span>
                </div>
              )}
            </>
          )}

          {!fanSearchMode && (
            <div style={styles.fanActions}>
              <button
                style={{ ...styles.outlineBtn, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                onClick={() => setFanSearchMode(true)}
              >
                <SearchIcon size={18} /> Search Manually
              </button>
              <button style={styles.outlineBtn} onClick={() => retakeCard(fanCard.id)}>Retake</button>
              <button
                style={{ ...styles.outlineBtn, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#f59e0b', borderColor: '#f59e0b' }}
                onClick={() => {
                  setContributeCardId(fanCard.id);
                  setShowContributePrompt(true);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                  <line x1="11" y1="8" x2="11" y2="14" />
                </svg>
                Can't Find Image
              </button>
              <button style={styles.removeBtn} onClick={() => removeCard(fanCard.id)}>Remove</button>
            </div>
          )}
        </div>
      )}

      {/* ========== DETAILS PHASE ========== */}
      {phase === PHASE.DETAILS && currentDetailCard && (() => {
        const card = currentDetailCard;
        const match = card.candidatesWithData[card.selectedMatchIndex] || {};

        return (
          <div style={styles.detailsPage}>
            <div style={styles.detailsHeader}>
              <button style={{ ...styles.iconBtn, backgroundColor: '#1a1a1a' }} onClick={() => setPhase(PHASE.GALLERY)}>
                <BackIcon />
              </button>
              <span style={styles.headerTitle}>
                Card {detailsSavedCount + 1} of {detailsCards.length}
              </span>
              <div style={{ width: 44 }} />
            </div>

            <div style={styles.detailsContent}>
              <div style={styles.cardNameSection}>
                <h3 style={styles.selectedCardName}>{match.name || 'Unknown Card'}</h3>
                <p style={styles.selectedCardSet}>
                  {match.setName} {match.number && `#${match.number}`}
                </p>
              </div>

              <div style={styles.imageComparison}>
                <div style={styles.imageBox}>
                  <span style={styles.imageLabel}>Your Scan</span>
                  <img src={card.croppedImage} alt="Scanned" style={styles.comparisonImg} />
                </div>
                <div style={styles.imageBox}>
                  <span style={styles.imageLabel}>Official</span>
                  {match.imageHigh || match.image ? (
                    <img src={match.imageHigh || match.image} alt="Official" style={styles.comparisonImg} onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div style={styles.noImagePlaceholder}><NoImageIcon /><span>No image</span></div>
                  )}
                </div>
              </div>

              <div style={styles.optionSection}>
                <label style={styles.optionLabel}>Variant</label>
                <div style={styles.optionGrid}>
                  {(card.availableVariants.length > 0 ? card.availableVariants : ['Normal']).map((v) => (
                    <button key={v} style={{ ...styles.optionBtn, ...(card.variant === v ? styles.optionBtnActive : {}) }} onClick={() => updateCard(card.id, { variant: v })}>{v}</button>
                  ))}
                </div>
              </div>

              <div style={styles.optionSection}>
                <label style={styles.optionLabel}>Specification</label>
                <div style={styles.optionGrid}>
                  <button style={{ ...styles.optionBtn, ...(!card.specification ? styles.optionBtnActive : {}) }} onClick={() => updateCard(card.id, { specification: null })}>None</button>
                  {['Shadowless', 'WOTC Stamped', 'Prerelease', 'STAFF', 'Winner', 'League Stamp', 'Build-A-Bear', 'Error/Misprint'].map((spec) => (
                    <button key={spec} style={{ ...styles.optionBtn, ...(card.specification === spec ? styles.optionBtnActive : {}) }} onClick={() => updateCard(card.id, { specification: spec })}>{spec}</button>
                  ))}
                </div>
              </div>

              <div style={styles.optionSection}>
                <label style={styles.optionLabel}>Grade Type</label>
                <div style={styles.gradeTypeToggle}>
                  <button style={{ ...styles.gradeTypeBtn, ...(card.gradeType === 'raw' ? styles.gradeTypeBtnActive : {}) }} onClick={() => updateCard(card.id, { gradeType: 'raw', condition: 'NM' })}>Raw</button>
                  <button style={{ ...styles.gradeTypeBtn, ...(card.gradeType === 'graded' ? styles.gradeTypeBtnActive : {}) }} onClick={() => updateCard(card.id, { gradeType: 'graded', condition: 'PSA 10' })}>Graded</button>
                </div>
              </div>

              {card.gradeType === 'graded' && (
                <div style={styles.optionSection}>
                  <label style={styles.optionLabel}>Grading Company</label>
                  <div style={styles.companyToggle}>
                    {['PSA', 'CGC', 'BGS'].map((company) => (
                      <button key={company} style={{ ...styles.companyBtn, ...(card.gradingCompany === company ? styles.companyBtnActive : {}) }} onClick={() => updateCard(card.id, { gradingCompany: company, condition: `${company} 10` })}>{company}</button>
                    ))}
                  </div>
                </div>
              )}

              <div style={styles.optionSection}>
                <label style={styles.optionLabel}>{card.gradeType === 'raw' ? 'Condition' : 'Grade'}</label>
                <div style={card.gradeType === 'graded' ? styles.gradeList : styles.optionGrid}>
                  {card.gradeType === 'raw' ? (
                    RAW_CONDITIONS.map((c) => (
                      <button key={c} style={{ ...styles.optionBtn, ...(card.condition === c ? styles.optionBtnActive : {}) }} onClick={() => updateCard(card.id, { condition: c })}>{c}</button>
                    ))
                  ) : (
                    getGradeOptions(card.gradingCompany).map((opt) => (
                      <button key={opt.value} style={{ ...styles.gradeOption, ...(card.condition === opt.value ? styles.gradeOptionActive : {}) }} onClick={() => updateCard(card.id, { condition: opt.value })}>{opt.label}</button>
                    ))
                  )}
                </div>
              </div>

              {/* Collection-only options (hidden in trade mode and quickscan) */}
              {!isTradeMode && !isQuickScan && (
                <>
                  <div style={styles.optionSection}>
                    <label style={styles.optionLabel}>Trading Status</label>
                    <div style={styles.gradeTypeToggle}>
                      <button style={{ ...styles.gradeTypeBtn, ...(card.tradingStatus === 'PC' ? styles.gradeTypeBtnActive : {}) }} onClick={() => updateCard(card.id, { tradingStatus: 'PC' })}>Personal Collection</button>
                      <button style={{ ...styles.gradeTypeBtn, ...(card.tradingStatus === 'TRADE' ? styles.gradeTypeBtnActive : {}), ...(isViewerOnly ? { opacity: 0.4 } : {}) }} onClick={() => { if (isViewerOnly) { setShowParentModal(true); } else { updateCard(card.id, { tradingStatus: 'TRADE' }); } }}>For Trade</button>
                    </div>
                  </div>

                  <div style={styles.optionSection}>
                    <label style={styles.optionLabel}>Add to Binder (Optional)</label>
                    <button style={styles.binderSelector} onClick={() => setShowBinderPicker(true)}>
                      <span>{card.binder ? card.binder.name : 'No binder selected'}</span>
                      <ChevronIcon />
                    </button>
                  </div>
                </>
              )}

              {isQuickScan ? (
                /* Quick Scan mode: advance to next card or get prices */
                (() => {
                  const handleQuickScanNext = () => {
                    // Build stripped version of current card before state update
                    const currentMatch = card.candidatesWithData?.[card.selectedMatchIndex] || {};
                    const strippedCurrent = {
                      id: card.id,
                      name: currentMatch.name || 'Unknown Card',
                      setName: currentMatch.setName || '',
                      setCode: currentMatch.setCode || '',
                      number: currentMatch.localId || currentMatch.number || '',
                      tcgId: currentMatch.tcgId || null,
                      digitalImage: currentMatch.imageHigh || currentMatch.image || null,
                      variant: card.variant || 'Normal',
                      condition: card.condition || 'NM',
                      status: 'saved',
                    };

                    // Kick off pricing immediately (non-blocking)
                    // By the time user finishes confirming remaining cards, this one is cached
                    fetchAndCacheSingleCard({
                      name: strippedCurrent.name,
                      setName: strippedCurrent.setName,
                      setNumber: strippedCurrent.number,
                      variant: strippedCurrent.variant,
                      grade: strippedCurrent.condition,
                    }).catch(() => {});

                    stripCardMemory(card.id);
                    const remaining = cards.filter(c => c.status === 'confirmed' && c.id !== card.id);
                    if (remaining.length === 0) {
                      // Build complete snapshot: already-saved cards + this one
                      const allSaved = [
                        ...cards.filter(c => c.status === 'saved'),
                        strippedCurrent,
                      ];
                      startPricingPhase(allSaved);
                    }
                  };

                  const remainingCount = cards.filter(c => c.status === 'confirmed' && c.id !== card.id).length;
                  return (
                    <button
                      style={styles.saveBtn}
                      onClick={handleQuickScanNext}
                    >
                      <CheckIcon size={18} />
                      {remainingCount > 0 ? 'Next Card' : 'Get Prices'}
                    </button>
                  );
                })()
              ) : isTradeMode ? (
                /* Trade mode: advance to next card or finish */
                (() => {
                  const handleNextOrDone = () => {
                    updateCard(card.id, { status: 'saved' });
                    const remaining = cards.filter(c => c.status === 'confirmed' && c.id !== card.id);
                    if (remaining.length === 0) {
                      setTimeout(() => addCardsToTrade(), 50);
                    }
                  };

                  return (
                    <button
                      style={styles.saveBtn}
                      onClick={handleNextOrDone}
                    >
                      <CheckIcon size={18} />
                      {cards.filter(c => c.status === 'confirmed' && c.id !== card.id).length > 0 ? 'Next Card' : 'Done'}
                    </button>
                  );
                })()
              ) : (
                <button
                  style={{ ...styles.saveBtn, opacity: isSaving ? 0.7 : 1 }}
                  onClick={saveCurrentCard}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <><div style={styles.spinnerSmall} /> Saving...</>
                  ) : (
                    <><CheckIcon size={18} /> Save to Collection</>
                  )}
                </button>
              )}
            </div>

            {/* Binder Picker Modal (collection mode only) */}
            {!isTradeMode && (
              <AnimatePresence>
                {showBinderPicker && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={styles.pickerOverlay} onClick={() => setShowBinderPicker(false)}>
                    <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }} style={styles.pickerSheet} onClick={(e) => e.stopPropagation()}>
                      <div style={styles.pickerHeader}>
                        <span style={styles.pickerTitle}>Select Binder</span>
                        <button style={styles.pickerClose} onClick={() => setShowBinderPicker(false)}><CloseIcon size={16} /></button>
                      </div>
                      <div style={styles.pickerOptions}>
                        <button style={{ ...styles.pickerOption, ...(card.binder === null ? styles.pickerOptionActive : {}) }} onClick={() => { updateCard(card.id, { binder: null }); setShowBinderPicker(false); }}>No binder</button>
                        {binders.map((binder) => (
                          <button key={binder.id} style={{ ...styles.pickerOption, ...(card.binder?.id === binder.id ? styles.pickerOptionActive : {}) }} onClick={() => { updateCard(card.id, { binder: { id: binder.id, name: binder.name } }); setShowBinderPicker(false); }}>{binder.name}</button>
                        ))}
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>
        );
      })()}

      {/* ========== DETAILS PHASE - Already saved card (back from next card) ========== */}
      {phase === PHASE.DETAILS && !currentDetailCard && detailsCards.length > 0 && (
        <div style={styles.donePage}>
          <div style={styles.detailsHeader}>
            <button style={{ ...styles.iconBtn, backgroundColor: '#1a1a1a' }} onClick={() => setPhase(PHASE.GALLERY)}>
              <BackIcon />
            </button>
            <span style={styles.headerTitle}>All Saved</span>
            <div style={{ width: 44 }} />
          </div>
          <div style={styles.doneContent}>
            <div style={styles.doneCheck}><CheckIcon size={40} /></div>
            <h2 style={styles.doneTitle}>This card was already saved to your collection</h2>
            <button style={styles.confirmBtn} onClick={() => setPhase(PHASE.DONE)}>Continue</button>
          </div>
        </div>
      )}

      {/* ========== PRICE RESULTS PHASE (Quick Scan) ========== */}
      {phase === PHASE.PRICE_RESULTS && (() => {
        const totalValue = pricingCards.reduce((sum, c) => sum + (c.price || 0), 0);
        const pricedCount = pricingCards.filter(c => c.price != null && c.price > 0).length;
        const totalCards = pricingCards.length;
        const progress = totalCards > 0 ? pricingProgress / totalCards : 0;

        return (
          <div style={styles.priceResultsPage}>
            {/* Header */}
            <div style={styles.priceResultsHeader}>
              <button style={{ ...styles.iconBtn, backgroundColor: '#1a1a1a' }} onClick={async () => { await stopCamera(); navigate(-1); }}>
                <CloseIcon />
              </button>
              <span style={styles.headerTitle}>Price Results</span>
              <button
                style={styles.exportBtn}
                onClick={handleExportPdf}
                disabled={isExportingPdf || pricingProgress < totalCards}
              >
                {isExportingPdf ? <div style={styles.spinnerSmall} /> : <ExportIcon />}
              </button>
            </div>

            {/* Progress bar */}
            {pricingProgress < totalCards && (
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${progress * 100}%` }} />
              </div>
            )}

            {/* Card list */}
            <div style={styles.priceResultsList}>
              {pricingCards.map((card, idx) => (
                <button
                  key={card.id}
                  style={styles.priceResultRow}
                  onClick={() => {
                    if (card.pricingData) setPricingDetailCard(card);
                  }}
                >
                  <span style={{ color: '#555', fontSize: 12, width: 24, textAlign: 'center', flexShrink: 0 }}>{idx + 1}</span>
                  {card.digitalImage ? (
                    <img src={card.digitalImage} alt="" style={styles.priceResultImg} loading="lazy" />
                  ) : (
                    <div style={{ ...styles.priceResultImg, backgroundColor: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <NoImageIcon />
                    </div>
                  )}
                  <div style={styles.priceResultInfo}>
                    <span style={styles.priceResultName}>{card.name}</span>
                    <span style={styles.priceResultSet}>{card.setName}{card.number ? ` #${card.number}` : ''}</span>
                    <span style={styles.priceResultMeta}>{card.condition} | {card.variant}</span>
                  </div>
                  <div style={styles.priceResultPrice}>
                    {card.priceLoading ? (
                      <div style={styles.spinnerSmall} />
                    ) : card.price != null ? (
                      <span style={styles.priceResultPriceText}>${card.price.toFixed(2)}</span>
                    ) : (
                      <span style={styles.priceResultPriceNA}>N/A</span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Footer */}
            <div style={styles.priceResultsFooter}>
              <div style={styles.totalRow}>
                <div>
                  <span style={styles.totalCount}>{pricedCount} of {totalCards} cards priced</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={styles.totalLabel}>Total Value</span>
                  <div style={styles.totalValue}>${totalValue.toFixed(2)}</div>
                </div>
              </div>
            </div>

            {/* Pricing detail overlay */}
            {pricingDetailCard && (
              <TradePricingOverlay
                card={pricingDetailCard}
                onClose={() => setPricingDetailCard(null)}
                onConditionSelect={(condKey) => {
                  const stats = pricingDetailCard.pricingData?.stats;
                  const isGraded = pricingDetailCard.pricingData?.is_graded === true;
                  let newPrice = null;
                  let newCondition = condKey;

                  if (isGraded) {
                    // Graded: condKey is the tier label like "PSA 10"
                    newPrice = stats?.[condKey]?.med ?? stats?.[condKey]?.avg ?? null;
                    newCondition = condKey;
                  } else {
                    // Raw: condKey is like "nm", "lp", etc.
                    newPrice = stats?.[condKey]?.med ?? stats?.[condKey]?.avg ?? null;
                    newCondition = condKey.toUpperCase();
                  }

                  setPricingCards(prev => prev.map(pc =>
                    pc.id === pricingDetailCard.id
                      ? { ...pc, price: newPrice, condition: newCondition }
                      : pc
                  ));
                  setPricingDetailCard(prev => prev ? { ...prev, price: newPrice, condition: newCondition } : null);
                }}
              />
            )}
          </div>
        );
      })()}

      {/* ========== DONE PHASE ========== */}
      {phase === PHASE.DONE && (
        <div style={styles.donePage}>
          <div style={styles.doneContent}>
            <div style={styles.doneCheck}><CheckIcon size={40} /></div>
            <h2 style={styles.doneTitle}>
              {savedCount} card{savedCount !== 1 ? 's' : ''} added to your collection!
            </h2>
            <div style={styles.doneActions}>
              <button style={styles.confirmBtn} onClick={() => navigate('/cards', { state: { cardAdded: true } })}>View Collection</button>
              <button style={styles.outlineBtn} onClick={() => { setCards([]); setSavedCount(0); setRetakeIndex(null); setPhase(PHASE.CAPTURE); }}>Scan More</button>
            </div>
          </div>
        </div>
      )}

      {/* Exit Confirmation Dialog */}
      <AnimatePresence>
        {showExitConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.pickerOverlay}
            onClick={() => setShowExitConfirm(false)}
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              style={styles.exitSheet}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={styles.exitTitle}>Unsaved Cards</h3>
              <p style={styles.exitDesc}>
                You have cards that haven't been saved yet. If you leave now, they will be lost.
              </p>
              <button style={styles.exitStayBtn} onClick={() => setShowExitConfirm(false)}>
                Stay
              </button>
              <button style={styles.exitLeaveBtn} onClick={confirmExit}>
                Leave and Discard
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Toast */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            style={styles.errorToast}
            onClick={() => setError(null)}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      <AddParentModal isOpen={showParentModal} onClose={() => setShowParentModal(false)} feature="trading" />

      {/* Contribute Card Image Prompt */}
      <AnimatePresence>
        {showContributePrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: 20,
            }}
            onClick={() => setShowContributePrompt(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              style={{
                backgroundColor: '#1a1a1a',
                borderRadius: 20,
                border: '1px solid #2a2a2a',
                width: '100%',
                maxWidth: 380,
                padding: '28px 24px',
                textAlign: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Badge icon */}
              <div style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'rgba(245, 158, 11, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                  <line x1="11" y1="8" x2="11" y2="14" />
                </svg>
              </div>

              <h3 style={{ color: '#fff', fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>
                Help Build the Database
              </h3>
              <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.5, margin: '0 0 20px' }}>
                Help CardChase become the largest community-driven Pokemon card database. Cards you add help every trainer find their cards faster.
              </p>

              {/* Steps */}
              <div style={{ textAlign: 'left', marginBottom: 20 }}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-start' }}>
                  <span style={{ color: '#0ea5e9', fontWeight: 700, fontSize: 14, minWidth: 20 }}>1.</span>
                  <span style={{ color: '#e0e0e0', fontSize: 13 }}>Find a clean digital image of the card</span>
                </div>
                <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-start' }}>
                  <span style={{ color: '#8b5cf6', fontWeight: 700, fontSize: 14, minWidth: 20 }}>2.</span>
                  <span style={{ color: '#e0e0e0', fontSize: 13 }}>Upload the image and enter card details (name, number, set, variant)</span>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 14, minWidth: 20 }}>3.</span>
                  <span style={{ color: '#e0e0e0', fontSize: 13 }}>Submit for review. Once approved, your Researcher badge levels up!</span>
                </div>
              </div>

              {/* Researcher badge preview */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '10px 16px',
                backgroundColor: 'rgba(14, 165, 233, 0.1)',
                borderRadius: 10,
                marginBottom: 20,
              }}>
                <div style={{ position: 'relative', width: 20, height: 20 }}>
                  <svg width="20" height="20" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#2a2a2a" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#0ea5e9" strokeWidth="3"
                      strokeDasharray="97.4" strokeDashoffset="39.0"
                      strokeLinecap="round" transform="rotate(-90 18 18)" />
                  </svg>
                  <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: 9, fontWeight: 800, color: '#0ea5e9' }}>1</span>
                </div>
                <span style={{ color: '#0ea5e9', fontSize: 12, fontWeight: 600 }}>Earn the Researcher badge</span>
              </div>

              {/* Actions */}
              {!showManualEntry ? (
                <>
                  <button
                    style={{
                      width: '100%',
                      padding: '14px 20px',
                      backgroundColor: '#f59e0b',
                      color: '#000',
                      border: 'none',
                      borderRadius: 12,
                      fontSize: 15,
                      fontWeight: 700,
                      cursor: 'pointer',
                      marginBottom: 10,
                    }}
                    onClick={() => {
                      const card = cards.find(c => c.id === contributeCardId);
                      setShowContributePrompt(false);
                      setShowManualEntry(false);
                      navigate('/add-card-manual', {
                        state: { photo: card?.croppedImage || null },
                      });
                    }}
                  >
                    Contribute Digital Image
                  </button>
                  <button
                    style={{
                      width: '100%',
                      padding: '14px 20px',
                      backgroundColor: 'transparent',
                      border: '1px solid #2a2a2a',
                      borderRadius: 12,
                      color: '#fff',
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: 'pointer',
                      marginBottom: 10,
                    }}
                    onClick={() => setShowManualEntry(true)}
                  >
                    Enter Card Info Manually
                  </button>
                  <button
                    style={{
                      width: '100%',
                      padding: '12px 20px',
                      backgroundColor: 'transparent',
                      color: '#6a6a6a',
                      border: 'none',
                      borderRadius: 12,
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                    onClick={() => { setShowContributePrompt(false); setShowManualEntry(false); }}
                  >
                    Not Now
                  </button>
                </>
              ) : (
                <>
                  <div style={{ textAlign: 'left', width: '100%' }}>
                    <h3 style={{ color: '#fff', fontSize: 16, fontWeight: 700, margin: '0 0 4px' }}>
                      Enter Card Info
                    </h3>
                    <p style={{ color: '#94a3b8', fontSize: 11, margin: '0 0 16px' }}>
                      Your photo will be used as the card image. Enter the details as they appear on the card.
                    </p>

                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 3 }}>Language</label>
                      <select
                        value={manualLanguage}
                        onChange={(e) => setManualLanguage(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          backgroundColor: '#1a1a1a',
                          border: '1px solid #2a2a2a',
                          borderRadius: 8,
                          color: '#fff',
                          fontSize: 14,
                          outline: 'none',
                        }}
                      >
                        <option value="en">English</option>
                        <option value="ja">Japanese</option>
                        <option value="ko">Korean</option>
                        <option value="zh-tw">Chinese (Traditional)</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                        <option value="it">Italian</option>
                        <option value="es">Spanish</option>
                        <option value="pt">Portuguese</option>
                      </select>
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 3 }}>Card Name *</label>
                      <input
                        type="text"
                        placeholder="e.g. Charizard ex"
                        value={manualName}
                        onChange={(e) => setManualName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          backgroundColor: '#1a1a1a',
                          border: '1px solid #2a2a2a',
                          borderRadius: 8,
                          color: '#fff',
                          fontSize: 14,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 3 }}>Card Number</label>
                        <input
                          type="text"
                          placeholder="e.g. 025/172"
                          value={manualNumber}
                          onChange={(e) => setManualNumber(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #2a2a2a',
                            borderRadius: 8,
                            color: '#fff',
                            fontSize: 14,
                            outline: 'none',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 3 }}>Set Name</label>
                        <input
                          type="text"
                          placeholder="e.g. Obsidian Flames"
                          value={manualSetName}
                          onChange={(e) => setManualSetName(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #2a2a2a',
                            borderRadius: 8,
                            color: '#fff',
                            fontSize: 14,
                            outline: 'none',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                    <button
                      style={{
                        flex: 1,
                        padding: '14px 20px',
                        backgroundColor: 'transparent',
                        border: '1px solid #2a2a2a',
                        borderRadius: 12,
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                      onClick={() => setShowManualEntry(false)}
                    >
                      Back
                    </button>
                    <button
                      style={{
                        flex: 2,
                        padding: '14px 20px',
                        backgroundColor: manualName.trim() ? '#0ea5e9' : '#1a1a1a',
                        border: 'none',
                        borderRadius: 12,
                        color: manualName.trim() ? '#fff' : '#6a6a6a',
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: manualName.trim() ? 'pointer' : 'default',
                        opacity: manualSaving ? 0.6 : 1,
                      }}
                      onClick={handleManualSave}
                      disabled={!manualName.trim() || manualSaving}
                    >
                      {manualSaving ? 'Saving...' : 'Add to Collection'}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// ICONS
// ============================================

const CloseIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const BackIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const CheckIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ExclamationIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const NoImageIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);

const SearchIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CollectionIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="16" height="14" rx="2" />
    <path d="M6 7V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2" />
    <path d="M22 10v8a2 2 0 0 1-2 2" />
  </svg>
);

const PhotosIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const ExportIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="12" y1="18" x2="12" y2="12" />
    <polyline points="9 15 12 12 15 15" />
  </svg>
);

// ============================================
// STYLES
// ============================================

const styles = {
  page: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: 9999,
  },
  cameraArea: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  loadingOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  frameOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  frame: {
    width: '75%',
    maxHeight: '60%',
    border: '3px solid rgba(255,255,255,0.8)',
    borderRadius: 12,
    position: 'relative',
  },
  corner: {
    position: 'absolute',
    width: 24,
    height: 24,
    borderLeft: '4px solid #0ea5e9',
    borderTop: '4px solid #0ea5e9',
  },
  topBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: 'calc(max(16px, env(safe-area-inset-top)) + 6px) 16px 14px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  iconBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(0,0,0,0.5)',
    border: 'none',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeBtn: {
    padding: '8px 16px',
    borderRadius: 20,
    backgroundColor: 'rgba(14, 165, 233, 0.9)',
    border: 'none',
    cursor: 'pointer',
  },
  badgeText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
  },
  bottomBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '20px 20px max(24px, env(safe-area-inset-bottom))',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Gallery - Vertical list
  galleryPage: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#0a0a0a',
  },
  galleryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'max(16px, env(safe-area-inset-top)) 16px 12px',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 17,
    fontWeight: 600,
  },
  nextBtn: {
    padding: '8px 20px',
    borderRadius: 20,
    border: 'none',
    backgroundColor: '#0ea5e9',
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  galleryList: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 12px max(24px, env(safe-area-inset-bottom))',
  },
  galleryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 0',
    borderBottom: '1px solid #1a1a1a',
    position: 'relative',
  },
  savedOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    color: '#22c55e',
    fontSize: 12,
    fontWeight: 600,
    zIndex: 2,
    backgroundColor: 'rgba(10, 10, 10, 0.6)',
    borderRadius: 8,
  },
  rowScanWrap: {
    width: 72,
    height: 100,
    borderRadius: 8,
    overflow: 'hidden',
    flexShrink: 0,
  },
  rowScanImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  rowFanWrap: {
    width: 100,
    height: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: 'none',
    backgroundColor: 'transparent',
    padding: 0,
    cursor: 'pointer',
  },
  rowFanPlaceholder: {
    width: 60,
    height: 84,
    borderRadius: 6,
    backgroundColor: '#1a1a1a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#444',
  },
  rowFanStack: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowFanCardBg: {
    width: 48,
    borderRadius: 5,
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
    opacity: 0.7,
    flexShrink: 0,
  },
  rowFanCardFront: {
    width: 60,
    borderRadius: 6,
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
    border: '2px solid #0ea5e9',
    flexShrink: 0,
    position: 'relative',
    zIndex: 5,
  },
  rowFanCardImg: {
    width: '100%',
    aspectRatio: '5/7',
    objectFit: 'cover',
    display: 'block',
  },
  rowFanCardEmpty: {
    width: '100%',
    aspectRatio: '5/7',
    backgroundColor: '#252525',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#444',
  },
  rowInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  rowInfoName: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowInfoSet: {
    color: '#888',
    fontSize: 13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowInfoNumber: {
    color: '#666',
    fontSize: 12,
  },
  rowChangeHint: {
    color: '#0ea5e9',
    fontSize: 12,
    fontWeight: 500,
    marginTop: 4,
    cursor: 'pointer',
  },

  // Card Fan (alternate match picker)
  fanPage: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#0a0a0a',
  },
  fanHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'max(16px, env(safe-area-inset-top)) 16px 12px',
  },
  fanScanWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '0 20px 16px',
  },
  fanScanLabel: {
    color: '#999',
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  fanScanImg: {
    width: 120,
    height: 168,
    borderRadius: 8,
    objectFit: 'cover',
    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
  },
  fanMatchLabel: {
    color: '#999',
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    textAlign: 'center',
    padding: '0 20px 8px',
  },
  fanScroll: {
    display: 'flex',
    gap: 12,
    padding: '0 20px',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    scrollSnapType: 'x mandatory',
    flex: 1,
    alignItems: 'flex-start',
  },
  fanCandidate: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    minWidth: 140,
    maxWidth: 140,
    padding: '12px 8px',
    borderRadius: 12,
    border: '2px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    scrollSnapAlign: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  fanCandidateActive: {
    border: '2px solid #0ea5e9',
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
  },
  fanCandidateImg: {
    width: 100,
    height: 140,
    borderRadius: 6,
    objectFit: 'cover',
  },
  fanCandidateImgPlaceholder: {
    width: 100,
    height: 140,
    borderRadius: 6,
    backgroundColor: '#252525',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#444',
  },
  fanCandidateName: {
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  fanCandidateSet: {
    color: '#888',
    fontSize: 11,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  fanCandidateNumber: {
    color: '#666',
    fontSize: 11,
    textAlign: 'center',
  },
  fanCandidateConf: {
    color: '#0ea5e9',
    fontSize: 13,
    fontWeight: 700,
  },
  fanActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '16px 20px max(24px, env(safe-area-inset-bottom))',
  },
  fanSearchBar: {
    display: 'flex',
    gap: 8,
    padding: '0 20px 12px',
  },
  fanSearchInput: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: 10,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#fff',
    fontSize: 15,
    outline: 'none',
  },
  fanSearchBtn: {
    width: 48,
    height: 48,
    borderRadius: 10,
    border: 'none',
    backgroundColor: '#0ea5e9',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  fanSearchResultsList: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 20px',
  },
  fanSearchResultRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 0',
    borderBottom: '1px solid #1a1a1a',
  },
  fanSearchResultImg: {
    width: 50,
    height: 70,
    borderRadius: 6,
    objectFit: 'cover',
    flexShrink: 0,
  },
  fanSearchResultInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  fanSearchSelectBtn: {
    padding: '8px 16px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#0ea5e9',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    flexShrink: 0,
  },
  translateToggleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    margin: '0 20px 8px',
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#aaa',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  },
  translatorPanel: {
    margin: '0 20px 12px',
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#1a1a1a',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  translatorLangRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  translatorSelect: {
    flex: 1,
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.12)',
    backgroundColor: '#252525',
    color: '#fff',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
  },
  translatorSwapBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.12)',
    backgroundColor: '#252525',
    color: '#fff',
    fontSize: 16,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  translatorInput: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.12)',
    backgroundColor: '#252525',
    color: '#fff',
    fontSize: 14,
    marginBottom: 10,
    boxSizing: 'border-box',
  },
  translatorGoBtn: {
    width: '100%',
    padding: '10px 16px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#6366f1',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 8,
  },
  translatorResultBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#252525',
    marginTop: 4,
  },
  translatorResultLabel: {
    color: '#888',
    fontSize: 11,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  translatorResultText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 600,
  },
  translatorUseBtn: {
    padding: '10px 16px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#22c55e',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
  },
  translatorRetryBtn: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.12)',
    backgroundColor: 'transparent',
    color: '#aaa',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  },
  translatorNote: {
    color: '#666',
    fontSize: 11,
    margin: '10px 0 0',
    lineHeight: 1.4,
  },
  confirmBtn: {
    width: '100%',
    padding: '16px 24px',
    borderRadius: 12,
    border: 'none',
    backgroundColor: '#22c55e',
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
  },
  outlineBtn: {
    width: '100%',
    padding: '14px 24px',
    borderRadius: 12,
    border: '1px solid #333',
    backgroundColor: '#1a1a1a',
    color: '#fff',
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
  },
  removeBtn: {
    width: '100%',
    padding: '12px 24px',
    borderRadius: 12,
    border: 'none',
    backgroundColor: 'transparent',
    color: '#ef4444',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },

  // Details
  detailsPage: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#0a0a0a',
  },
  detailsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'max(12px, env(safe-area-inset-top)) 16px 8px',
  },
  detailsContent: {
    flex: 1,
    padding: '8px 20px',
    overflowY: 'auto',
  },
  cardNameSection: {
    textAlign: 'center',
    marginBottom: 10,
  },
  selectedCardName: {
    color: '#fff',
    fontSize: 17,
    fontWeight: 600,
    margin: '0 0 2px 0',
  },
  selectedCardSet: {
    color: '#666',
    fontSize: 13,
    margin: 0,
  },
  imageComparison: {
    display: 'flex',
    gap: 10,
    marginBottom: 14,
    justifyContent: 'center',
  },
  imageBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    maxWidth: 130,
  },
  imageLabel: {
    color: '#999',
    fontSize: 10,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  comparisonImg: {
    width: 130,
    height: 182,
    borderRadius: 6,
    objectFit: 'cover',
    backgroundColor: '#1a1a1a',
  },
  noImagePlaceholder: {
    width: 130,
    height: 182,
    borderRadius: 6,
    backgroundColor: '#1a1a1a',
    border: '1px dashed #333',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    color: '#666',
    fontSize: 11,
  },
  optionSection: {
    marginBottom: 14,
  },
  optionLabel: {
    display: 'block',
    color: '#999',
    fontSize: 11,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 6,
  },
  optionGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  optionBtn: {
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#999',
    fontSize: 13,
    fontWeight: 500,
  },
  optionBtnActive: {
    border: '1px solid #0ea5e9',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    color: '#0ea5e9',
  },
  gradeTypeToggle: {
    display: 'flex',
    gap: 6,
  },
  gradeTypeBtn: {
    flex: 1,
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#999',
    fontSize: 13,
    fontWeight: 500,
  },
  gradeTypeBtnActive: {
    border: '1px solid #0ea5e9',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    color: '#0ea5e9',
  },
  companyToggle: {
    display: 'flex',
    gap: 6,
  },
  companyBtn: {
    flex: 1,
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#999',
    fontSize: 13,
    fontWeight: 600,
  },
  companyBtnActive: {
    border: '1px solid #0ea5e9',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    color: '#0ea5e9',
  },
  gradeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  gradeOption: {
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#999',
    fontSize: 13,
    fontWeight: 500,
    textAlign: 'left',
  },
  gradeOptionActive: {
    border: '1px solid #0ea5e9',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    color: '#0ea5e9',
  },
  binderSelector: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid #2a2a2a',
    backgroundColor: '#1a1a1a',
    color: '#fff',
    fontSize: 14,
  },
  saveBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '14px 24px',
    borderRadius: 12,
    border: 'none',
    backgroundColor: '#22c55e',
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
    marginTop: 16,
    marginBottom: 'max(16px, env(safe-area-inset-bottom))',
  },
  pickerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'flex-end',
    zIndex: 200,
  },
  pickerSheet: {
    width: '100%',
    maxHeight: '60%',
    backgroundColor: '#1a1a1a',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    overflow: 'hidden',
  },
  pickerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #2a2a2a',
  },
  pickerTitle: {
    color: '#fff',
    fontSize: 17,
    fontWeight: 600,
  },
  pickerClose: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#333',
    border: 'none',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pickerOptions: {
    padding: '8px 0',
    maxHeight: 300,
    overflowY: 'auto',
  },
  pickerOption: {
    width: '100%',
    padding: '16px 20px',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#fff',
    fontSize: 16,
    textAlign: 'left',
  },
  pickerOptionActive: {
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    color: '#0ea5e9',
  },

  // Done
  donePage: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#0a0a0a',
    alignItems: 'center',
    justifyContent: 'center',
  },
  doneContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
    padding: 40,
  },
  doneCheck: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#22c55e',
  },
  doneTitle: {
    color: '#fff',
    fontSize: 22,
    fontWeight: 600,
    textAlign: 'center',
    margin: 0,
  },
  doneActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
  },

  // Price Results (Quick Scan)
  priceResultsPage: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#0a0a0a',
  },
  priceResultsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'max(16px, env(safe-area-inset-top)) 16px 12px',
  },
  exportBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#1a1a1a',
    border: 'none',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  progressBar: {
    height: 3,
    backgroundColor: '#1a1a1a',
    marginBottom: 2,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#0ea5e9',
    borderRadius: 2,
    transition: 'width 0.3s ease',
  },
  priceResultsList: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 12px',
  },
  priceResultRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 4px',
    width: '100%',
    background: 'none',
    border: 'none',
    borderBottom: '1px solid #1a1a1a',
    cursor: 'pointer',
    textAlign: 'left',
  },
  priceResultImg: {
    width: 44,
    height: 62,
    borderRadius: 5,
    objectFit: 'cover',
    flexShrink: 0,
  },
  priceResultInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    minWidth: 0,
  },
  priceResultName: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  priceResultSet: {
    color: '#888',
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  priceResultMeta: {
    color: '#666',
    fontSize: 11,
  },
  priceResultPrice: {
    flexShrink: 0,
    minWidth: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  priceResultPriceText: {
    color: '#22c55e',
    fontSize: 16,
    fontWeight: 700,
  },
  priceResultPriceNA: {
    color: '#555',
    fontSize: 13,
    fontWeight: 500,
  },
  priceResultsFooter: {
    padding: '16px 20px max(20px, env(safe-area-inset-bottom))',
    borderTop: '1px solid #1a1a1a',
    backgroundColor: '#0a0a0a',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  totalLabel: {
    display: 'block',
    color: '#888',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 2,
  },
  totalValue: {
    color: '#22c55e',
    fontSize: 28,
    fontWeight: 700,
  },
  totalCount: {
    color: '#666',
    fontSize: 13,
  },

  // Shared
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid rgba(255,255,255,0.2)',
    borderTopColor: '#0ea5e9',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  spinnerSmall: {
    width: 16,
    height: 16,
    border: '2px solid rgba(255,255,255,0.2)',
    borderTopColor: '#0ea5e9',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorToast: {
    position: 'fixed',
    bottom: 100,
    left: 20,
    right: 20,
    padding: '14px 20px',
    borderRadius: 12,
    backgroundColor: 'rgba(239, 68, 68, 0.95)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'center',
    zIndex: 9999,
  },

  // Exit confirmation
  exitSheet: {
    width: '100%',
    backgroundColor: '#1a1a1a',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: '24px 20px max(24px, env(safe-area-inset-bottom))',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  exitTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  exitDesc: {
    color: '#999',
    fontSize: 14,
    margin: 0,
    lineHeight: 1.5,
  },
  exitStayBtn: {
    width: '100%',
    padding: '16px 24px',
    borderRadius: 12,
    border: 'none',
    backgroundColor: '#0ea5e9',
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
  },
  exitLeaveBtn: {
    width: '100%',
    padding: '14px 24px',
    borderRadius: 12,
    border: 'none',
    backgroundColor: 'transparent',
    color: '#ef4444',
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
  },

  // Collection Picker (trade mode)
  collectionPickPage: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#0a0a0a',
  },
  collectionPickHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'max(16px, env(safe-area-inset-top)) 16px 12px',
  },
  collectionSearchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    margin: '0 16px 12px',
    padding: '10px 14px',
    borderRadius: 10,
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
    color: '#888',
  },
  collectionSearchInput: {
    flex: 1,
    border: 'none',
    backgroundColor: 'transparent',
    color: '#fff',
    fontSize: 15,
    outline: 'none',
  },
  collectionGrid: {
    flex: 1,
    overflowY: 'auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(105px, 1fr))',
    gap: 10,
    padding: '0 12px max(24px, env(safe-area-inset-bottom))',
    alignContent: 'start',
  },
  collectionCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    padding: 6,
    borderRadius: 10,
    border: '2px solid transparent',
    backgroundColor: '#1a1a1a',
    cursor: 'pointer',
    position: 'relative',
  },
  collectionCardSelected: {
    border: '2px solid #0ea5e9',
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
  },
  collectionCardImg: {
    width: '100%',
    aspectRatio: '5/7',
    objectFit: 'cover',
    borderRadius: 6,
    display: 'block',
  },
  collectionCardNoImg: {
    width: '100%',
    aspectRatio: '5/7',
    borderRadius: 6,
    backgroundColor: '#252525',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#444',
  },
  collectionCardCheck: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#0ea5e9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
  },
  collectionCardName: {
    color: '#fff',
    fontSize: 11,
    fontWeight: 600,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  collectionCardSet: {
    color: '#666',
    fontSize: 10,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },

  // Photos button (capture bottom bar)
  photosBtn: {
    width: 80,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    border: 'none',
    backgroundColor: 'rgba(0,0,0,0.4)',
    borderRadius: 12,
    padding: '8px 0',
    color: '#fff',
    cursor: 'pointer',
  },

  // Library Crop
  libraryCropPage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 100,
  },
  libraryCropTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'max(16px, env(safe-area-inset-top)) 16px 12px',
    zIndex: 10,
  },
  libraryCropProgress: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
  },
  libraryCropArea: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  libraryCropImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  libraryCropFrameOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  libraryCropBottom: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px max(24px, env(safe-area-inset-bottom))',
    gap: 12,
    zIndex: 10,
  },
  libraryCropBackBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '12px 16px',
    borderRadius: 10,
    border: '1px solid #333',
    backgroundColor: '#1a1a1a',
    color: '#fff',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  libraryCropSkipBtn: {
    padding: '12px 20px',
    borderRadius: 10,
    border: '1px solid #333',
    backgroundColor: 'transparent',
    color: '#999',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  libraryCropCropBtn: {
    padding: '12px 20px',
    borderRadius: 10,
    border: 'none',
    backgroundColor: '#0ea5e9',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default BulkScanner;
